<script setup lang="ts">
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'

  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'
  import BetaBadge from '@/components/common/BetaBadge.vue'

  import AutoSavingIndicator from '@/components/admin/AutoSavingIndicator.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import AgentActionWidget from '@/components/agent-actions/AgentActionWidget.vue'
  import CreateAppointmentModal from '@/components/app/cases/appointmentCreationModal.vue'
  import DocumentUpload from '@/components/app/cases/documents/DocumentUpload.vue'
  import SelectDocumentsDialog from '@/components/app/cases/documents/SelectDocumentsDialog.vue'
  import TemplateDialog from '@/components/app/cases/documents/TemplateDialog.vue'
  import LegacyCaseHeader from '@/components/app/cases/legacy/LegacyCaseHeader.vue'
  import LegacyEntityForm from '@/components/app/cases/legacy/LegacyEntityForm.vue'
  import EmbeddableOfficePreview from '@/components/app/office/EmbeddableOfficePreview.vue'
  import TrashIcon from '@/components/icons/TrashIcon.vue'
  import NewPdfViewer from '@/components/pdf/NewPdfViewer.vue'
  import PDFViewer from '@/components/pdf/pdfViewer.vue'
  import SelectPdfFormsDialog from '@/components/pdf/SelectPdfFormsDialog.vue'
  import SignModal from '@/components/pdf/SignModal.vue'
  import EmailDeliveryStatusModal from '@/components/app/cases/delivery/EmailDeliveryStatusModal.vue'
  import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
  } from '@/components/ui/accordion'
  import { Button } from '@/components/ui/button'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { Switch } from '@/components/ui/switch'
  import { useToast } from '@/components/ui/toast'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { useDocumentThumbnail } from '@/composables/useDocumentThumbnail'
  import {
    type AttachedDocument,
    DocumentTypeIdEnum,
    type FormDocumentProperties,
    type QuestionnaireDocumentProperties
  } from '@/composables/useEmailPopover'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { useOfficeDocument } from '@/composables/useOfficeDocument'
  import { useTranslation } from '@/composables/useTranslation'
  import router from '@/router'
  import RichTextEditor from '@/shared/components/RichTextEditor.vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore.ts'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import { useNewPdfStore } from '@/store/newPdfStore'
  import { useSignatures } from '@/store/signatureStore'
  import {
    AiSettingsService,
    AppointmentProposalsService,
    AppointmentsService,
    AppointmentStatusEnum,
    type CustomerCase as Case,
    CasesService,
    type CustomerAppointment,
    type CustomerAppointmentProposal,
    type CustomerEmail,
    type CustomerEmailTemplate,
    type CustomerForm,
    type CustomerQuestionnaire,
    type CustomerQuestionnaireSubmission,
    DocumentsService,
    DocumentStatusEnum,
    DocumentTypeEnum,
    EmailsService,
    EmailTemplatesService,
    FlagEnum,
    FormsService,
    type PatchedCustomerCaseRequest,
    type Document as PdfDocument,
    PdfFormTemplatesService,
    PdfFormSubmissionsService,
    QuestionnairesService,
    QuestionnaireSubmissionsService,
    type TForm,
    type CustomerUser as User,
    type PDFFormTemplate,
    UnifiedDocumentTemplatesService
  } from '@/types/generated'
  import { useCaseChannelSubscription } from '@/composables/useChannelSubscription'
  import {
    Calendar1Icon,
    CalendarIcon,
    CalendarPlusIcon,
    CircleXIcon,
    Clock5Icon,
    CloudDownloadIcon,
    EllipsisVerticalIcon,
    Eye,
    FileIcon,
    InfoIcon,
    MapPinIcon,
    PencilIcon,
    UserIcon,
    CheckIcon,
    MailIcon,
    XCircleIcon,
    AlertTriangleIcon,
    ShieldXIcon,
    EyeIcon,
    MousePointerClickIcon,
    Loader2
  } from 'lucide-vue-next'
  import { AccordionHeader } from 'radix-vue'
  import { computed, h, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
  import { ROUTES } from '@/router/routes'

  // Extended type for questionnaire submissions with additional properties needed for the UI
  type RequestedQuestionnaire = CustomerQuestionnaireSubmission & {
    document_type: string
    filename: string
    created_at: string
  }

  // Union type for all requested documents (questionnaires and PDFs)
  type RequestedDocuments = RequestedQuestionnaire | PdfDocument

  const { t } = useTranslation({
    en: {
      acceptance: 'Acceptance',
      addDocument: 'Add document',
      aiChat: 'AI Chat',
      allDocumentsCompleted: 'Great, all requested documents have been completed.',
      API: 'API',
      APPOINTMENT: 'Appointment',
      appointmentDetails: 'Appointment Details',
      appointments: 'Appointments',
      bookingLinkSent: 'Booking link sent',
      cancel: 'Cancel',
      cancelAppointment: 'Cancel',
      cancelledAppointment: 'Cancelled',
      confirmAppointment: 'Confirm',
      caseWorker: 'Case Worker',
      CHAT_BOT: 'Chat Bot',
      city: 'City',
      clerk: 'Clerk',
      noClerkAssigned: 'Not assigned',
      client: 'Client',
      closeQuestionnaire: 'Close Questionnaire',
      completeRequest: 'Complete Request',
      completed: 'COMPLETED',
      confirmedAppointment: 'Confirmed',
      conflictOfInterest: 'Conflict of Interest',
      contactPerson: 'Contact Person',
      contactPersonFirstName: 'Contact Person First Name',
      contactPersonLastName: 'Contact Person Last Name',
      contactPersonTitle: 'Contact Person Title',
      createAppointment: 'Create Appointment',
      createCase: 'Create Case',
      createFile: 'Create File',
      createKleosCase: 'Create Kleos Case',
      createLinkToDocumentTemplate: 'Create link to document template',
      createLinkToQuestionnaire: 'Create link to questionnaire',
      createLinkQuestionnaireDescription:
        'Share questionnaires with external people to collect additional information that will be attached to this inquiry. Warning: Do not reuse these links beyond this inquiry. To create reusable public links, use Settings.',
      createLinkDocumentTemplateDescription:
        'Share document templates with external people to collect filled-out forms that will be attached to this inquiry. Warning: Do not reuse these links beyond this inquiry. To create reusable public links, use Settings.',
      created: 'Created',
      date: 'Date',
      delete: 'Delete',
      deleteRequest: 'Delete Request',
      deleteRequestConfirmation: 'Are you sure you want to delete this request?',
      deleteRequestWarning: 'This action cannot be undone.',
      deleted: 'DELETED',
      documents: 'Documents',
      documentTemplate: 'Document Template',
      pdfFormTemplate: 'PDF Form Template',
      documentUploaded: 'Document Uploaded',
      documentUploadedSuccessfully: 'The document was successfully uploaded.',
      documentsAndQuestionnaires: 'Document Templates & Questionnaires',
      download: 'Download',
      edit: 'Edit',
      downloadAllDocuments: 'Download All Documents',
      email: 'Email',
      EMAIL: 'Email',
      emailContent: 'Email content...',
      emailHistory: 'Email History',
      entities: 'Entities',
      exportRAMICROFile: 'Export RA-MICRO File',
      firstName: 'First Name',
      general: 'General',
      lawSpecificData: 'Law-Specific Data',
      houseNumber: 'House',
      inPerson: 'In Person',
      inProgress: 'IN PROGRESS',
      inquiry: 'Inquiry',
      institution: 'Institution',
      institutionName: 'Institution Name',
      internalNotes: 'Internal Notes',
      lastName: 'Last Name',
      lawField: 'Law field',
      lawTopic: 'Law topic',
      legalArea: 'Legal Area',
      legalForm: 'Legal Form',
      legalInsurance: 'Legal Insurance',
      legalInsuranceName: 'Legal Insurance Name',
      legalInsuranceNumber: 'Legal Insurance Number',
      legalPerson: 'Legal person',
      location: 'Location',
      MANUAL: 'Manual',
      name: 'Name',
      naturalPerson: 'Natural person',
      new: 'NEW',
      noAppointments: 'No appointments available',
      noAttachments: 'No attachments added yet.',
      noDataAvailable: 'No data available',
      noDocumentsReceived: 'No documents have been received for this request yet.',
      noEmailHistory: 'No email history available.',
      noRequestedDocuments: 'No documents have been requested yet.',
      noResultsFound: 'No results found',
      noService: 'No Service',
      notesPlaceholder: 'Add internal notes about this case...',
      oclock: '',
      online: 'Online-Appointment',
      opponent: 'Opponent',
      pending: 'Pending',
      phone: 'Phone',
      PHONE: 'Phone',
      postalCode: 'Postal Code',
      preview: 'Preview',
      QUESTIONNAIRE: 'Questionnaire',
      questionnaire: 'Questionnaire',
      questionnaireDescription: 'Select one or more questionnaires',
      questionnaireSubmission: 'Questionnaire Submission',
      questionnaireTitle: 'Questionnaire',
      receivedDocuments: 'Received Documents',
      rejected: 'REJECTED',
      rejectRequest: 'Reject Request',
      rejection: 'Rejection',
      reopenRequest: 'Set Request to "Open"',
      requestedDocuments: 'Requested Documents',
      enableEmailReminder: 'Email Reminders',
      enableEmailReminderTitle: 'Enable email reminders',
      enableEmailReminderDescription:
        'When Email Reminders are enabled, this toggle is enabled by default for all cases, you can disable it for this specific case.',
      requestFrom: 'Request from',
      requestInProgress: 'Request in Progress',
      searchAppointments: 'Search appointments...',
      searchQuestionnaire: 'Search questionnaire...',
      searchTemplate: 'Search template',
      selectTemplate: 'Select Template',
      selectTemplateDescription: 'Choose an email template from the list',
      send: 'Send',
      service: 'Service',
      sortBy: 'Sort by',
      status: 'Status',
      street: 'Street',
      subject: 'Subject',
      summarizeWithAI: 'Summarize inquiry with AI',
      summaryOfInquiry: 'Summary of Inquiry',
      telephoneAppointment: 'Telephone Appointment',
      tentativeAppointment: 'Tentative',
      time: 'Time',
      title: 'Title',
      type: 'Channel',
      unknown: 'Unknown',
      uploadDocuments: 'Upload Documents',
      uploadDocumentsDescription: 'Please upload your documents here.',
      viewDetails: 'View Details',
      error: 'Error',
      failedToUpdateEmailReminder: 'Failed to update the email reminder setting',
      failedToSyncEntityData: 'Failed to sync entity data. Please try again.',
      deliveryEventSent: 'Sent',
      deliveryEventDelivered: 'Delivered',
      deliveryEventOpened: 'Opened',
      deliveryEventClicked: 'Clicked',
      deliveryEventHardBounced: 'Not Deliverable',
      deliveryEventSoftBounced: 'Not Deliverable',
      deliveryEventBlocked: 'Not Deliverable',
      deliveryEventSpam: 'Spam',
      deliveryEventUnsubscribed: 'Unsubscribed',
      deliveryEventUnknown: 'Unknown',
      deliveryEventPending: 'Checking delivery...',
      warning: 'Warning',
      noClientEmailWarning:
        'No client email address is set. Please add a client email before sending.',
      requiredFieldsNotFilled: 'Please fill all required fields in PDF forms.',
      signaturesNotFilled: 'Please fill all signature fields in PDF forms.'
    },
    de: {
      acceptance: 'Aufnahme',
      addDocument: 'Dokumente hinzufügen',
      aiChat: 'KI Chat',
      allDocumentsCompleted: 'Prima, alle angeforderten Dokumente wurden bereits ausgefüllt.',
      API: 'API',
      APPOINTMENT: 'Terminbuchung',
      appointmentDetails: 'Termindetails',
      appointments: 'Termine',
      bookingLinkSent: 'Buchungslink gesendet',
      cancel: 'Abbrechen',
      cancelAppointment: 'Absagen',
      cancelledAppointment: 'Abgesagt',
      confirmAppointment: 'Bestätigen',
      caseWorker: 'Sachbearbeiter',
      CHAT_BOT: 'Chat Bot',
      city: 'Ort',
      clerk: 'Sachbearbeiter',
      noClerkAssigned: 'Nicht zugewiesen',
      client: 'Mandant',
      closeQuestionnaire: 'Fragebogen abschließen',
      completeRequest: 'Anfrage abschließen',
      completed: 'ABGESCHLOSSEN',
      confirmedAppointment: 'Bestätigt',
      conflictOfInterest: 'Interessenkollision',
      contactPerson: 'Kontaktperson',
      contactPersonFirstName: 'Kontaktperson Vorname',
      contactPersonLastName: 'Kontaktperson Nachname',
      contactPersonTitle: 'Kontaktperson Titel',
      createAppointment: 'Termin erstellen',
      createCase: 'Akte anlegen',
      createFile: 'Akte anlegen',
      createKleosCase: 'Kleos Akte anlegen',
      createLinkToDocumentTemplate: 'Link zu Dokumentenvorlage erzeugen',
      createLinkToQuestionnaire: 'Link zu Fragebogen erzeugen',
      createLinkQuestionnaireDescription:
        'Teilen Sie Fragebögen mit externen Personen, um zusätzliche Informationen zu sammeln, die dieser Anfrage beigefügt werden. Verwenden Sie diese Links nicht über diese Anfrage hinaus erneut. Um wiederverwendbare öffentliche Links zu erstellen, nutzen Sie die Einstellungen.',
      createLinkDocumentTemplateDescription:
        'Teilen Sie Dokumentenvorlagen mit externen Personen, um ausgefüllte Dokumente zu erhalten, die dieser Anfrage beigefügt werden. Verwenden Sie diese Links nicht über diese Anfrage hinaus erneut. Um wiederverwendbare öffentliche Links zu erstellen, nutzen Sie die Einstellungen.',
      created: 'Erstellt',
      date: 'Datum',
      delete: 'Löschen',
      deleteRequest: 'Anfrage löschen',
      deleteRequestConfirmation: 'Sind Sie sicher, dass Sie diese Anfrage löschen möchten?',
      deleteRequestWarning: 'Dieser Vorgang kann nicht rückgängig gemacht werden.',
      deleted: 'GELÖSCHT',
      documents: 'Dokumente',
      documentTemplate: 'Dokumentenvorlage',
      pdfFormTemplate: 'PDF-Formularvorlage',
      documentUploaded: 'Dokument hochgeladen',
      documentUploadedSuccessfully: 'Das Dokument wurde erfolgreich hochgeladen.',
      documentsAndQuestionnaires: 'Dokumentenvorlagen & Fragebögen',
      download: 'Herunterladen',
      edit: 'Bearbeiten',
      downloadAllDocuments: 'Alle Dokumente Herunterladen',
      email: 'E-Mail',
      EMAIL: 'E-Mail',
      emailContent: 'E-Mail-Inhalt...',
      emailHistory: 'E-Mail Verlauf',
      entities: 'Beteiligte',
      exportRAMICROFile: 'RA-MICRO Datei exportieren',
      firstName: 'Vorname',
      general: 'Allgemein',
      lawSpecificData: 'Rechtsspezifische Daten',
      houseNumber: 'Hausnummer',
      inPerson: 'Vor Ort',
      inProgress: 'IN BEARBEITUNG',
      inquiry: 'Anfrage',
      institution: 'Institution',
      institutionName: 'Institutionsname',
      internalNotes: 'Interne Notizen',
      lastName: 'Nachname',
      lawField: 'Rechtsgebiet',
      lawTopic: 'Rechtsthema',
      legalArea: 'Rechtsgebiet',
      legalForm: 'Rechtsform',
      legalInsurance: 'Rechtsschutzversicherung',
      legalInsuranceName: 'Versicherungsgesellschaft',
      legalInsuranceNumber: 'Versicherungsnummer',
      legalPerson: 'Rechtliche Person',
      location: 'Ort',
      MANUAL: 'Manuell',
      name: 'Name',
      naturalPerson: 'Natürliche Person',
      new: 'NEU',
      noAppointments: 'Keine Termine vorhanden',
      noAttachments: 'Noch keine Anhänge hinzugefügt.',
      noDataAvailable: 'Keine Daten verfügbar',
      noDocumentsReceived: 'Sie haben bisher zu dieser Anfrage noch keine Dokumente erhalten.',
      noEmailHistory: 'Kein E-Mail-Verlauf verfügbar.',
      noRequestedDocuments: 'Es wurden bisher keine Dokumente angefordert.',
      noResultsFound: 'Keine Ergebnisse gefunden',
      noService: 'Kein Service',
      notesPlaceholder: 'Interne Notizen zu diesem Fall hinzufügen...',
      oclock: 'Uhr',
      online: 'Online-Termin',
      opponent: 'Gegner',
      pending: 'Ausstehend',
      phone: 'Telefon',
      PHONE: 'Telefon',
      postalCode: 'PLZ',
      preview: 'Ansehen',
      QUESTIONNAIRE: 'Fragebogen',
      questionnaire: 'Fragebogen',
      questionnaireDescription: 'Wählen Sie einen oder mehrere Fragebögen aus',
      questionnaireSubmission: 'Fragebogen Einreichung',
      questionnaireTitle: 'Fragebogen',
      receivedDocuments: 'Erhaltene Dokumente',
      rejected: 'ABGELEHNT',
      rejectRequest: 'Anfrage ablehnen',
      rejection: 'Ablehnung',
      reopenRequest: 'Anfrage auf "offen" setzen',
      requestedDocuments: 'Angeforderte Dokumente',
      enableEmailReminder: 'E-Mail Erinnerungen für Mandant',
      enableEmailReminderTitle: 'E-Mail Erinnerungen aktivieren',
      enableEmailReminderDescription:
        'Schalten Sie hier E-Mail Erinnerungen für diese Anfrage ein oder aus.',
      requestFrom: 'Anfrage von',
      requestInProgress: 'Anfrage in Bearbeitung',
      searchAppointments: 'Termine durchsuchen...',
      searchQuestionnaire: 'Fragebogen suchen...',
      searchTemplate: 'Vorlage suchen',
      selectTemplate: 'Vorlage auswählen',
      selectTemplateDescription: 'Wählen Sie eine E-Mail Vorlage aus der Liste',
      send: 'Senden',
      service: 'Service',
      sortBy: 'Sortieren nach',
      status: 'Status',
      street: 'Straße',
      subject: 'Betreff',
      summarizeWithAI: 'Anfrage mit KI zusammenfassen',
      summaryOfInquiry: 'Zusammenfassung der Anfrage',
      telephoneAppointment: 'Telefon-Termin',
      tentativeAppointment: 'Unbestätigt',
      time: 'Zeit',
      title: 'Titel',
      type: 'Kanal',
      unknown: 'Unbekannt',
      uploadDocuments: 'Dokumente hochladen',
      uploadDocumentsDescription: 'Dokumente hier hochladen bitte.',
      viewDetails: 'Details anzeigen',
      error: 'Fehler',
      failedToUpdateEmailReminder: 'Aktualisierung der E-Mail-Erinnerungsfunktion fehlgeschlagen',
      failedToSyncEntityData:
        'Fehler beim Synchronisieren der Daten. Bitte versuchen Sie es erneut.',
      deliveryEventSent: 'Gesendet',
      deliveryEventDelivered: 'Zugestellt',
      deliveryEventOpened: 'Geöffnet',
      deliveryEventClicked: 'Angeklickt',
      deliveryEventHardBounced: 'Nicht zustellbar',
      deliveryEventSoftBounced: 'Nicht zustellbar',
      deliveryEventBlocked: 'Nicht zustellbar',
      deliveryEventSpam: 'Als Spam markiert',
      deliveryEventUnsubscribed: 'Abgemeldet',
      deliveryEventUnknown: 'Unbekannt',
      deliveryEventPending: 'Prüfe Zustellung',
      warning: 'Warnung',
      noClientEmailWarning:
        'Keine E-Mail-Adresse des Mandanten hinterlegt. Bitte fügen Sie eine E-Mail-Adresse hinzu, bevor Sie eine E-Mail senden.',
      requiredFieldsNotFilled: 'Bitte alle Pflichtfelder im PDF-Formular ausfüllen.',
      signaturesNotFilled: 'Bitte alle Unterschriftsfelder im PDF-Formular ausfüllen.'
    }
  })

  const { toast } = useToast()
  const newPdfStore = useNewPdfStore()
  const signatureStore = useSignatures()

  const caseStore = useCaseStore()
  const userStore = useUserStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)
  const officeEditingFlag = useFeatureFlag(FlagEnum.OFFICE_DOCUMENT_EDITING)
  const {
    isOfficeFile: isOfficeFileUtil,
    isEditableDocument: isEditableDocumentUtil,
    openOfficeEditor
  } = useOfficeDocument()
  const { getThumbnailUrl } = useDocumentThumbnail()
  const isLoadingDocuments = ref(false)

  // Email functionality properties
  const emailSubject = ref('')
  const emailContent = ref('')
  const emailTemplates = ref<CustomerEmailTemplate[]>([])
  const selectedEmailTemplate = ref<CustomerEmailTemplate | null>(null)
  const selectedTemplateId = ref('')
  const emailAttachments = ref<AttachedDocument[]>([])
  const isSendingEmail = ref(false)

  // Email history variables
  const emails = ref<CustomerEmail[]>([])
  const isLoadingEmails = ref(true)
  const pendingEmailUuids = ref<Set<string>>(new Set())
  const emailHistoryAccordionValue = ref('email-history')

  // Document selection state
  const showDocumentAttachDialog = ref(false)
  const showQuestionnaireAttachDialog = ref(false)
  const selectedDocument = ref<CustomerForm | CustomerQuestionnaire | null>(null)
  const user = computed(() => userStore.user as User)
  const nonEmptyLawSpecificMappers = computed(() => caseStore.nonEmptyLawSpecificMappers)
  const entities = computed(() => caseRecord.value?.data?.entities)

  // Navigation logic
  const pages = computed(() => {
    return [
      {
        title: () => h('span', t('general')),
        route: ROUTES.APP.CASES.LEGACY.CASE_DETAIL.replace(':uuid', caseRecord.value?.uuid ?? '')
      },
      {
        title: () =>
          h('span', { class: 'flex gap-2 items-center' }, [
            t('lawSpecificData'), // Text for the second page
            nonEmptyLawSpecificMappers.value.length > 0
              ? h('span', { class: 'badge-square' }, `${nonEmptyLawSpecificMappers.value.length}`)
              : null
          ]),
        route: ROUTES.APP.CASES.CASE_DETAIL.LAW_SPECIFIC_DATA.replace(
          ':uuid',
          caseRecord.value?.uuid ?? ''
        )
      },
      {
        title: () =>
          h('span', { class: 'flex gap-2 items-center' }, [
            t('entities'),
            entities.value && entities.value?.length > 0
              ? h('span', { class: 'badge-square' }, `${entities.value.length}`)
              : null
          ]),
        route: ROUTES.APP.CASES.CASE_DETAIL.ENTITIES.replace(
          ':uuid',
          caseRecord.value?.uuid ?? ''
        ).replace('/:entityUuid?', '')
      },
      {
        title: () => h('span', t('aiChat')),
        route: ROUTES.APP.CASES.CASE_DETAIL.AI_CHAT.replace(':uuid', caseRecord.value?.uuid ?? '')
      }
    ]
  })

  const activePage = computed(() => {
    const currentPath = router.currentRoute.value.fullPath
    return pages.value.find((page) => {
      // For entities page, match both with and without entityUuid
      if (page.route.includes('entities')) {
        return currentPath.includes('entities')
      }
      // For law-specific-data page
      if (page.route.includes('law-specific-data')) {
        return currentPath.includes('law-specific-data')
      }
      // For chat page
      if (page.route.includes('chat')) {
        return currentPath.includes('chat')
      }
      // For general page
      return page.route === currentPath
    })
  })

  const getTabId = (route: string) => {
    if (route.includes('entities')) return 'entities'
    if (route.includes('law-specific-data')) return 'law-specific-data'
    if (route.includes('chat')) return 'ai-chat'
    return 'allgemein'
  }

  const selectedClerk = ref('')

  // Law field and topics
  const { fetchLawFields, getLawFieldOptions, getLawTopicOptions } = useLawFieldStore()
  const selectedLawField = ref('')
  const selectedLawTopic = ref('')
  const lawFieldOptions = computed<Option[]>(() => {
    return getLawFieldOptions(user.value)
  })
  const lawTopicOptions = computed<Option[]>(() => {
    // Only show law topics if the selected law field is available to the user
    const userLawFields = Array.isArray(user.value?.org?.law_fields)
      ? user.value.org.law_fields
      : []
    if (!selectedLawField.value || !userLawFields.includes(selectedLawField.value)) {
      return []
    }
    return getLawTopicOptions(selectedLawField.value)
  })

  const isAppointmentCreateModalOpen = ref(false)

  const isDocumentUploadModalOpen = ref(false)
  const showUnifiedTemplateModal = ref(false)
  const showQuestionnaireModal = ref(false)
  const unifiedTemplates = ref<any[]>([])

  const questionnaires = ref<any[]>([])
  const receivedDocsSortBy = ref('name')
  const requestedDocsSortBy = ref('name')

  const loadingQuestionnaires = ref(false)
  const loadingUnifiedTemplates = ref(false)

  // PDF Viewer state
  const showPdfViewer = ref(false)
  const showPdfEditor = ref(false)
  const currentPdfUrl = ref('')
  const currentPdfFilename = ref('')
  const currentDocumentUuid = ref('')

  const showOfficePreview = ref(false)
  const currentOfficeDocument = ref<PdfDocument | null>(null)

  // Questionnaire submission modal state
  const showQuestionnaireSubmissionModal = ref(false)
  const selectedQuestionnaireSubmission = ref<RequestedQuestionnaire | null>(null)

  // AI Summary state
  const inquirySummary = ref('')
  const isGeneratingSummary = ref(false)

  // Delivery status modal state
  const showDeliveryStatusModal = ref(false)
  const selectedEmailForDeliveryStatus = ref<CustomerEmail | null>(null)

  // Form refs
  const notesFormRef = ref()

  // Get questionnaire submissions for received documents
  const receivedDocuments = computed(() => {
    const receivedDocs = []

    // Add questionnaire submissions from caseRecord.documents as pdf
    // Add requested pdfs also from caseRecord.documents
    if (caseRecord.value?.documents && Array.isArray(caseRecord.value.documents)) {
      const pdfDocuments = caseRecord.value.documents.filter(
        (doc) =>
          (doc.document_type === DocumentTypeEnum.DOKUMENTENVORLAGE &&
            doc.document_status === DocumentStatusEnum.RECEIVED) ||
          (doc.document_type === DocumentTypeEnum.FRAGEBOGEN &&
            doc.document_status === DocumentStatusEnum.RECEIVED) ||
          (doc.document_type === DocumentTypeEnum.KI_GENERIERTES_DOKUMENT &&
            doc.document_status === DocumentStatusEnum.COMPLETED) ||
          doc.document_type === DocumentTypeEnum.UPLOAD
      )
      receivedDocs.push(...pdfDocuments)
    }

    // Add completed PDF form submissions
    if (
      caseRecord.value?.pdf_form_submissions &&
      Array.isArray(caseRecord.value.pdf_form_submissions)
    ) {
      const completedSubmissions = caseRecord.value.pdf_form_submissions
        .filter((submission) => submission.submission_status === 'completed')
        .map((submission) => ({
          uuid: submission.uuid,
          title: submission.template_name || 'PDF Form',
          filename: `${submission.template_name || 'form'}.pdf`,
          created_at: submission.completed_at || submission.created_at,
          download: {
            url: submission.flattened_pdf || submission.processed_pdf,
            filename: `${submission.template_name || 'form'}.pdf`
          },
          images: submission.images || [],
          document_type: DocumentTypeIdEnum.PDF_FORM_SUBMISSION,
          document_status: 'completed'
        }))
      receivedDocs.push(...completedSubmissions)
    }

    // Sort the combined results
    const sorted = receivedDocs.sort((a, b) => {
      if (receivedDocsSortBy.value === 'name') {
        if (!a.title || !b.title) return 0
        return a.title.localeCompare(b.title)
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })

    return sorted
  })

  // Get form requests for requested documents
  const requestedDocuments = computed<RequestedDocuments[]>(() => {
    const requestedDocs: RequestedDocuments[] = []

    // Add questionnaire requests
    if (
      caseRecord.value?.questionnaire_submissions &&
      Array.isArray(caseRecord.value.questionnaire_submissions)
    ) {
      const submissions = caseRecord.value.questionnaire_submissions.filter(
        (submission) => !submission.closed
      )

      // Map submissions to the RequestedQuestionnaire type
      const questionnaireItems = submissions.map((submission) => ({
        ...submission,
        document_type: DocumentTypeEnum.FRAGEBOGEN,
        filename: submission.title || 'Fragebogen',
        created_at: submission.created_at,
        title: submission.title || 'Fragebogen'
      })) as RequestedQuestionnaire[]

      requestedDocs.push(...questionnaireItems)
    }

    // Add pdf requests
    if (caseRecord.value?.documents && Array.isArray(caseRecord.value.documents)) {
      const pdfDocs = caseRecord.value.documents.filter(
        (document) =>
          document.document_type === DocumentTypeEnum.DOKUMENTENVORLAGE &&
          document.document_status === DocumentStatusEnum.REQUESTED
      ) as (PdfDocument & { upload_origin?: string })[]

      requestedDocs.push(...pdfDocs)
    }

    // Sort by date (newest first)
    return requestedDocs.sort((a, b) => {
      if (requestedDocsSortBy.value === 'name') {
        const aTitle = a.title || ''
        const bTitle = b.title || ''
        return aTitle.localeCompare(bTitle)
      }

      // Use safe date handling with fallbacks
      const aDate = a.created_at ? new Date(a.created_at).getTime() : 0
      const bDate = b.created_at ? new Date(b.created_at).getTime() : 0
      return bDate - aDate
    })
  })

  const caseCreated = computed(() => {
    return caseRecord.value?.created_at
      ? new Date(caseRecord.value.created_at).toLocaleString('de-DE', {
          weekday: 'short',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric'
        })
      : ''
  })

  const caseType = computed(() => {
    if (!caseRecord.value?.type) return ''
    switch (caseRecord.value.type) {
      case 'API':
        return t('API')
      case 'CHAT BOT':
        return t('CHAT_BOT')
      case 'QUESTIONNAIRE':
        return t('QUESTIONNAIRE')
      case 'EMAIL':
        return t('EMAIL')
      case 'MANUAL':
        return t('MANUAL')
      case 'PHONE':
        return t('PHONE')
      case 'APPOINTMENT':
        return t('APPOINTMENT')
      default:
        return ''
    }
  })

  const clerkOptions = computed<Option[]>(() => {
    if (!caseRecord.value?.org?.users) return []
    const options = caseRecord.value.org.users.map((user) => ({
      value: user.uuid,
      title: `${user.first_name} ${user.last_name}`
    }))
    // add option for no clerk assigned
    options.unshift({
      value: '',
      title: t('noClerkAssigned')
    })
    return options
  })

  const hasLegalInsurance = computed({
    get() {
      return caseRecord.value?.data?.client?.legal_insurance ?? undefined
    },
    set(value) {
      if (caseRecord.value?.data?.client) {
        caseRecord.value.data.client.legal_insurance = value
      }
    }
  })

  const legalInsuranceName = computed({
    get() {
      return caseRecord.value?.data?.client?.legal_insurance_name ?? undefined
    },
    set(value) {
      if (caseRecord.value?.data?.client) {
        caseRecord.value.data.client.legal_insurance_name = value
      }
    }
  })

  const legalInsuranceNumber = computed({
    get() {
      return caseRecord.value?.data?.client?.legal_insurance_number ?? undefined
    },
    set(value) {
      if (caseRecord.value?.data?.client) {
        caseRecord.value.data.client.legal_insurance_number = value
      }
    }
  })

  const internalNotes = computed({
    get() {
      return caseRecord.value?.note ?? ''
    },
    set(value) {
      if (caseRecord.value) {
        caseRecord.value.note = value
      }
    }
  })

  const submitCaseUpdate = async (updates: Partial<PatchedCustomerCaseRequest>) => {
    if (!caseRecord.value?.uuid) return
    try {
      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value.uuid,
        requestBody: updates
      })
    } catch (error) {
      console.error('Error saving case update:', error)
    }
  }

  const handleCaseInfoSave = (values: any) => {
    const updates: Partial<PatchedCustomerCaseRequest> = {}
    if (values.clerk !== undefined) {
      updates.clerk_id = values.clerk === '' ? null : values.clerk
    }

    if (values.lawField !== undefined || values.lawTopic !== undefined) {
      updates.data = { law: {} } as any

      if (values.lawField !== undefined) {
        ;(updates.data as any).law.field = values.lawField
      }
      if (values.lawTopic !== undefined) {
        const options = getLawTopicOptions(values.lawField)
        if (!options.find((option) => option.value === values.lawTopic)) {
          ;(updates.data as any).law.topic = ''
        } else {
          ;(updates.data as any).law.topic = values.lawTopic
        }
      }
    }

    submitCaseUpdate(updates)
  }

  const handleInsuranceInfoSave = (values: any) => {
    const updates: Partial<PatchedCustomerCaseRequest> = {}
    updates.data = { client: {} } as any
    if (values.has_legal_insurance !== undefined) {
      ;(updates.data as any).client.legal_insurance = values.has_legal_insurance
    }
    if (values.legal_insurance_name !== undefined) {
      ;(updates.data as any).client.legal_insurance_name = values.legal_insurance_name
    }
    if (values.legal_insurance_number !== undefined) {
      ;(updates.data as any).client.legal_insurance_number = values.legal_insurance_number
    }
    submitCaseUpdate(updates)
  }

  const handleNotesSave = (values: any) => {
    const updates: Partial<PatchedCustomerCaseRequest> = {}
    if (values.notes !== undefined) {
      updates.note = values.notes
    }
    submitCaseUpdate(updates)
  }

  const refreshCaseRecord = async () => {
    if (caseRecord.value?.uuid) {
      await caseStore.loadCase(caseRecord.value.uuid)
    }
  }

  const handleFileUploaded = async (documentUuid: string) => {
    if (caseRecord.value?.uuid) {
      await caseStore.loadCase(caseRecord.value.uuid)
      toast({
        title: t('documentUploaded'),
        description: t('documentUploadedSuccessfully'),
        variant: 'default'
      })
      // Close modal after successful upload
      isDocumentUploadModalOpen.value = false
    }
  }

  const handleDocumentUpdated = async (updatedDocument: any) => {
    console.log('Document updated:', updatedDocument)

    // Update the document in the case record if it exists
    if (caseRecord.value?.documents) {
      const docIndex = caseRecord.value.documents.findIndex((d) => d.uuid === updatedDocument.uuid)
      if (docIndex !== -1) {
        caseRecord.value.documents[docIndex] = {
          ...caseRecord.value.documents[docIndex],
          ...updatedDocument
        }
        console.log('Updated document in case record:', caseRecord.value.documents[docIndex])
      }
    }

    // Update the selected document if this is the document being viewed
    if (
      selectedDocument.value &&
      ((selectedDocument.value as any).uuid === updatedDocument.uuid ||
        (selectedDocument.value as any).id === updatedDocument.uuid)
    ) {
      selectedDocument.value = {
        ...selectedDocument.value,
        ...updatedDocument
      }
      console.log('Updated selected document:', selectedDocument.value)
    }

    // Refresh the case data to get the latest changes
    await refreshCaseRecord()
  }

  const fetchQuestionnaires = async () => {
    loadingQuestionnaires.value = true
    try {
      const response = await QuestionnairesService.questionnairesList()
      // Filter out Jupus templates - only show actual questionnaires
      questionnaires.value = (response.results || []).filter((q) => !q.is_jupus_template)
    } catch (error) {
      console.error('Error fetching questionnaires:', error)
      toast({
        title: 'Fehler',
        description: 'Fragebögen konnten nicht geladen werden',
        variant: 'destructive'
      })
    } finally {
      loadingQuestionnaires.value = false
    }
  }

  const handleGenerateUnifiedTemplateLink = async () => {
    showUnifiedTemplateModal.value = true
    await fetchUnifiedTemplates()
  }

  const fetchUnifiedTemplates = async () => {
    loadingUnifiedTemplates.value = true
    try {
      const response = await UnifiedDocumentTemplatesService.unifiedDocumentTemplatesList()
      unifiedTemplates.value = response.results || []
    } catch (error) {
      console.error('Error fetching unified templates:', error)
      toast({
        title: 'Fehler',
        description: 'Vorlagen konnten nicht geladen werden',
        variant: 'destructive'
      })
    } finally {
      loadingUnifiedTemplates.value = false
    }
  }

  const handleGenerateQuestionnaireLink = async () => {
    showQuestionnaireModal.value = true
    await fetchQuestionnaires()
  }

  const isEmailReminderForOutstandingDocumentsEnabled = computed({
    get() {
      return caseRecord.value?.send_outstanding_document_email_reminder ?? false
    },
    set(value: boolean) {
      handleEmailReminderForOutstandingDocumentsChange(value)
    }
  })

  const loadingChangeEmailReminderStatus = ref(false)

  const handleEmailReminderForOutstandingDocumentsChange = async (value: boolean) => {
    if (!caseRecord.value?.uuid) return
    caseStore.caseRecord!.send_outstanding_document_email_reminder = value
    loadingChangeEmailReminderStatus.value = true
    try {
      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value.uuid,
        requestBody: {
          send_outstanding_document_email_reminder: value
        }
      })
      caseStore.caseRecord!.send_outstanding_document_email_reminder = value
    } catch (error) {
      console.error('Error updating case:', error)
      toast({
        title: t('error'),
        description: t('failedToUpdateEmailReminder'),
        variant: 'destructive'
      })
      caseStore.caseRecord!.send_outstanding_document_email_reminder = !value
    }
    loadingChangeEmailReminderStatus.value = false
  }

  const createLinkUnifiedTemplate = async (template: any) => {
    try {
      let shareableLink: string

      if (template.template_type === 'legacy_form') {
        // Handle legacy form template
        const response = await FormsService.formsThirdPartyTokenRetrieve({
          id: template.id
        })
        shareableLink = `${window.location.origin}/client/portal/external/pdf/${response.creation_token}/${caseRecord.value?.uuid}`
      } else if (template.template_type === 'pdf_form') {
        // Handle PDF form template
        const response = await PdfFormSubmissionsService.pdfFormSubmissionsExternalCreate({
          requestBody: {
            template_uuid: template.id,
            case_uuid: caseRecord.value?.uuid || ''
          }
        })
        shareableLink = response.submission_url
      } else {
        throw new Error('Unknown template type')
      }

      // Try to use modern clipboard API first
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
        const clipboardItem = new ClipboardItem({
          'text/plain': new Blob([shareableLink], { type: 'text/plain' })
        })
        await navigator.clipboard.write([clipboardItem])
      } else {
        // Fallback to older clipboard API
        await navigator.clipboard.writeText(shareableLink)
      }

      toast({
        title: 'Link kopiert',
        description: 'Der Link wurde in die Zwischenablage kopiert',
        variant: 'default'
      })

      showUnifiedTemplateModal.value = false
    } catch (error) {
      console.error('Error generating shareable link:', error)
      toast({
        title: 'Fehler',
        description: 'Der Link konnte nicht generiert werden',
        variant: 'destructive'
      })
    } finally {
      await refreshCaseRecord()
    }
  }

  const createLinkQuestionnaire = async (questionnaire: any) => {
    try {
      const response = await QuestionnairesService.questionnairesThirdPartyCreate({
        requestBody: {
          questionnaire_id: questionnaire.uuid,
          case_id: caseRecord.value?.uuid || ''
        }
      })
      const shareableLink = `${window.location.origin}/client/portal/external/questionnaire/${response.short_id}?access_token=${response.third_party_access_token}`

      // Try to use modern clipboard API first
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
        const clipboardItem = new ClipboardItem({
          'text/plain': new Blob([shareableLink], { type: 'text/plain' })
        })
        await navigator.clipboard.write([clipboardItem])
      } else {
        // Fallback to older clipboard API
        await navigator.clipboard.writeText(shareableLink)
      }

      toast({
        title: 'Link kopiert',
        description: 'Der Link wurde in die Zwischenablage kopiert',
        variant: 'default'
      })

      showQuestionnaireModal.value = false
    } catch (error) {
      console.error('Error generating shareable link:', error)
      toast({
        title: 'Fehler',
        description: 'Der Link konnte nicht generiert werden',
        variant: 'destructive'
      })
    } finally {
      await refreshCaseRecord()
    }
  }

  // Format date for appointments
  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  // Format time for appointments
  const formatTime = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return ''
    const start = new Date(startDate)
    const end = new Date(endDate)
    const startHours = start.getHours().toString().padStart(2, '0')
    const startMinutes = start.getMinutes().toString().padStart(2, '0')
    const endHours = end.getHours().toString().padStart(2, '0')
    const endMinutes = end.getMinutes().toString().padStart(2, '0')
    return `${startHours}:${startMinutes} ${t('oclock')} - ${endHours}:${endMinutes} ${t('oclock')}`
  }

  // Format date for documents with time
  const formatDocumentDate = (dateString: string) => {
    const date = new Date(dateString)
    const formattedDate = date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${formattedDate} ${hours}:${minutes} Uhr`
  }

  // Get appointment status text
  const getAppointmentStatusText = (appointment: CustomerAppointment) => {
    switch (appointment.status) {
      case AppointmentStatusEnum.CONFIRMED:
        return t('confirmedAppointment')
      case AppointmentStatusEnum.TENTATIVE:
        return t('tentativeAppointment')
      case AppointmentStatusEnum.CANCELLED:
        return t('cancelledAppointment')
    }
  }

  const deleteAppointmentProposal = async (proposal: CustomerAppointmentProposal) => {
    if (!proposal.uuid) return
    try {
      await AppointmentProposalsService.appointmentProposalsDestroy({
        uuid: proposal.uuid
      })
      await refreshCaseRecord()
    } catch (error) {
      console.error('Error deleting proposal:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Error deleting proposal:${error}`
      })
    }
  }

  const cancelAppointmentBooking = async (appointment: CustomerAppointment) => {
    if (!appointment.uuid) return

    const originalStatus = appointment.status
    // @ts-expect-error - Optimistic update on read-only generated type
    appointment.status = AppointmentStatusEnum.CANCELLED

    try {
      await AppointmentsService.appointmentsCancelCreate({
        uuid: appointment.uuid
      })
      await refreshCaseRecord()
    } catch (error) {
      // @ts-expect-error - Revert Optimistic update on read-only generated type
      appointment.status = originalStatus
      console.error('Error cancelling appointment:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Error cancelling appointment:${error}`
      })
    }
  }

  const confirmAppointmentBooking = async (appointment: CustomerAppointment) => {
    if (!appointment.uuid) return

    const originalStatus = appointment.status
    // @ts-expect-error - Optimistic update on read-only generated type
    appointment.status = AppointmentStatusEnum.CONFIRMED

    try {
      await AppointmentsService.appointmentsConfirmCreate({
        uuid: appointment.uuid
      })
      await refreshCaseRecord()
      toast({
        title: 'Erfolg',
        description: 'Termin wurde bestätigt',
        variant: 'default'
      })
    } catch (error) {
      // @ts-expect-error - Revert Optimistic update on read-only generated type
      appointment.status = originalStatus
      console.error('Error confirming appointment:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Error confirming appointment: ${error}`
      })
    }
  }

  const deleteAppointmentBooking = async (appointment: CustomerAppointment) => {
    if (!appointment.uuid) return
    try {
      await AppointmentsService.appointmentsDestroy({
        uuid: appointment.uuid
      })
      await refreshCaseRecord()
    } catch (error) {
      console.error('Error deleting appointment:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Error deleting appointment:${error}`
      })
    }
  }

  // Fetch email templates
  const fetchEmailTemplates = async () => {
    if (emailTemplates.value.length > 0) return
    try {
      const response = await EmailTemplatesService.emailTemplatesList()
      emailTemplates.value = response.results as CustomerEmailTemplate[]

      // Select the first template by default if available
      if (emailTemplates.value.length > 0) {
        const firstTemplate = emailTemplates.value[0]
        selectedTemplateId.value = firstTemplate.uuid
        selectedEmailTemplate.value = firstTemplate
        emailSubject.value = firstTemplate.subject || ''
        emailContent.value = firstTemplate.html || ''
      }
    } catch (error) {
      console.error('Error fetching email templates:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'E-Mail-Vorlagen konnten nicht geladen werden'
      })
    }
  }

  // Handle template selection
  const handleTemplateSelect = (templateId: string) => {
    selectedTemplateId.value = templateId
    if (templateId) {
      const template = emailTemplates.value.find((t) => t.uuid === templateId)
      if (template) {
        selectedEmailTemplate.value = template
        emailSubject.value = template.subject || ''
        emailContent.value = template.html || ''
      }
    }
  }

  // Handle document attachment
  const handleAttachDocumentTemplate = async () => {
    showDocumentAttachDialog.value = true
  }

  const handleAttachQuestionnaire = async () => {
    showQuestionnaireAttachDialog.value = true
    await fetchQuestionnaires()
  }

  const handleDocumentsSelect = async (documents: TForm[]) => {
    // First, remove all the PDF template documents that are not in the new selection
    emailAttachments.value = emailAttachments.value.filter((attachment) => {
      // Keep non-PDF documents
      if (attachment.documentType.id !== DocumentTypeIdEnum.PDF_TEMPLATE) {
        return true
      }

      // For PDF documents, check if they're in the new selection
      const formProps = attachment.documentProperties as FormDocumentProperties
      return documents.some((doc) => doc.id === formProps.id)
    })

    // Then add any new documents that aren't already attached
    const currentFormIds = emailAttachments.value
      .filter((doc) => doc.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE)
      .map((doc) => (doc.documentProperties as FormDocumentProperties).id)

    // Find documents that aren't already attached
    const newDocuments = documents.filter((doc) => !currentFormIds.includes(doc.id))

    // Convert new documents to the attachment format and add them
    if (newDocuments.length > 0) {
      const convertedDocs = newDocuments.map((doc) => {
        return {
          documentType: {
            id: DocumentTypeIdEnum.PDF_TEMPLATE,
            name: 'Dokumentenvorlage'
          },
          documentProperties: {
            id: doc.id,
            organisation: doc.organisation,
            created_at: doc.created_at,
            modified_at: doc.modified_at,
            tag: doc.tag,
            title: doc.title,
            description: doc.description,
            file: doc.file,
            due_at: doc.due_at,
            document_name: doc.document_name,
            annotationData: {},
            signatures: {}
          } as FormDocumentProperties
        } as AttachedDocument
      })

      // Create a new array to ensure reactivity
      emailAttachments.value = [...emailAttachments.value, ...convertedDocs]

      // Load PDFs using the batch loader
      if (newDocuments.length > 0) {
        newPdfStore
          .batchLoadPdfs(newDocuments)
          .then((results) => {
            emailAttachments.value.forEach((attachment: AttachedDocument) => {
              attachment.documentProperties =
                attachment.documentProperties as FormDocumentProperties
              if (
                attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE &&
                results[attachment.documentProperties.id] === true
              ) {
                const currentAnnotations = newPdfStore.getAnnotations(
                  attachment.documentProperties.id
                )
                attachment.documentProperties.annotationData = [...currentAnnotations]

                const formSignatures = signatureStore.getFormSignatures(
                  attachment.documentProperties.id
                )
                attachment.documentProperties.signatures = formSignatures
              }
            })
          })
          .catch((error) => {
            console.error('Error in batch loading PDFs:', error)
          })
      }
    }
  }

  const handleQuestionnairesSelect = (questionnaires: CustomerQuestionnaire[]) => {
    if (!questionnaires || questionnaires.length === 0) return

    questionnaires.forEach((questionnaire) => {
      // Check if questionnaire is already attached
      const exists = emailAttachments.value.some(
        (attachment) =>
          attachment.documentType.id === DocumentTypeIdEnum.QUESTIONNAIRE &&
          (attachment.documentProperties as QuestionnaireDocumentProperties).id ===
            questionnaire.uuid
      )

      if (!exists) {
        const attachment: AttachedDocument = {
          documentType: {
            id: DocumentTypeIdEnum.QUESTIONNAIRE,
            name: 'Fragebogen'
          },
          documentProperties: {
            ...questionnaire,
            id: questionnaire.uuid
          } as QuestionnaireDocumentProperties
        }

        emailAttachments.value.push(attachment)
      }
    })
  }

  const handleQuestionnairePreview = (questionnaire: CustomerQuestionnaire) => {
    if (questionnaire && questionnaire.uuid) {
      window.open(`/client/portal/questionnaire/preview/${questionnaire.uuid}`, '_blank')
    }
  }

  const handleUnifiedTemplatePreview = (template: any) => {
    if (template && template.file) {
      window.open(template.file, '_blank')
    }
  }

  const openPdfEditor = (form: AttachedDocument) => {
    selectedDocument.value = form.documentProperties as CustomerForm
    showPdfEditor.value = true
  }

  const removeAttachment = (attachment: AttachedDocument) => {
    // Get the document identifier based on its type
    let docIdentifier: string | undefined

    if (attachment.documentType?.id === DocumentTypeIdEnum.QUESTIONNAIRE) {
      docIdentifier = (attachment.documentProperties as QuestionnaireDocumentProperties)?.uuid
    } else {
      docIdentifier = (attachment.documentProperties as FormDocumentProperties)?.id
    }

    if (!docIdentifier) {
      console.error('Could not determine document identifier for removal', attachment)
      return
    }

    emailAttachments.value = emailAttachments.value.filter((attachment) => {
      if (attachment.documentType?.id === DocumentTypeIdEnum.QUESTIONNAIRE) {
        return (
          (attachment.documentProperties as QuestionnaireDocumentProperties)?.uuid !== docIdentifier
        )
      } else {
        return (attachment.documentProperties as FormDocumentProperties)?.id !== docIdentifier
      }
    })

    // Also remove from the PDF store if it was loaded
    if (
      attachment.documentType?.id === DocumentTypeIdEnum.PDF_TEMPLATE &&
      docIdentifier &&
      newPdfStore.selectedPdfs.has(docIdentifier)
    ) {
      newPdfStore.removePdf(docIdentifier)
    }
  }

  const getAttachmentKey = (attachment: AttachedDocument): string => {
    if (attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
      return `form-${(attachment.documentProperties as FormDocumentProperties).id}`
    } else {
      return `questionnaire-${(attachment.documentProperties as QuestionnaireDocumentProperties).id}`
    }
  }

  const formatAttachmentName = (attachment: AttachedDocument): string => {
    if (attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
      const formProps = attachment.documentProperties as FormDocumentProperties
      const fileName = formProps.document_name?.split('/').pop()
      return formProps.title || fileName || 'Unnamed Document'
    } else {
      const questionnaireProps = attachment.documentProperties as QuestionnaireDocumentProperties
      return questionnaireProps.title || 'Unnamed Questionnaire'
    }
  }

  const syncEntityDataToBackend = async (): Promise<boolean> => {
    try {
      const clientData = caseRecord.value?.data?.client
      if (!clientData) {
        return true
      }

      const updatePayload: PatchedCustomerCaseRequest = {
        data: {
          client: {
            email: clientData.email || '',
            first_name: clientData.first_name || '',
            last_name: clientData.last_name || ''
          }
        } as any
      }

      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value?.uuid || '',
        requestBody: updatePayload
      })

      return true
    } catch (error) {
      console.error('Error syncing entity data to backend:', error)
      toast({
        title: t('error'),
        description: t('failedToSyncEntityData'),
        variant: 'destructive'
      })
      return false
    }
  }

  const handleSendEmail = async () => {
    if (isSendingEmail.value) return

    // Check if client email is missing
    const clientEmail = caseRecord.value?.data?.client?.email
    if (!clientEmail || clientEmail.trim() === '') {
      toast({
        title: t('warning') || 'Warning',
        description:
          t('noClientEmailWarning') ||
          'No client email address is set. Please add a client email before sending.',
        variant: 'destructive'
      })
      return
    }

    try {
      isSendingEmail.value = true

      // Sync entity data (email, first_name, last_name) to backend before sending email
      const syncSuccess = await syncEntityDataToBackend()
      if (!syncSuccess) {
        return
      }

      // Ensure all PDF annotations are up to date in the attachments
      const pdfAttachments = emailAttachments.value.filter(
        (attachment) => attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE
      )

      for (const attachment of pdfAttachments) {
        const formProps = attachment.documentProperties as FormDocumentProperties
        const currentAnnotations = newPdfStore.getAnnotations(formProps.id)
        formProps.annotationData = [...currentAnnotations]

        // add signatures to the form properties, might be redundant to the selecting of documents but it is required for the pdf editors.
        const formSignatures = signatureStore.getFormSignatures(formProps.id)
        formProps.signatures = formSignatures
      }

      // Make the API call
      const sentEmailResponse = await CasesService.casesSendEmailCreate({
        uuid: caseRecord.value?.uuid || '',
        requestBody: {
          subject: emailSubject.value,
          body: emailContent.value,
          attachments: emailAttachments.value,
          auto_reject: selectedEmailTemplate.value?.type === 'reject'
        }
      })

      // Add the sent email UUID to pending list for loading state
      if (sentEmailResponse.uuid) {
        pendingEmailUuids.value.add(sentEmailResponse.uuid)
        console.log('📤 Email sent, added to pending:', sentEmailResponse.uuid)

        // Set a timeout to automatically remove from pending after 10 minutes
        // in case delivery events don't arrive (fallback safety measure)
        setTimeout(() => {
          if (pendingEmailUuids.value.has(sentEmailResponse.uuid)) {
            pendingEmailUuids.value.delete(sentEmailResponse.uuid)
            console.log('⏰ Auto-removed email from pending after timeout:', sentEmailResponse.uuid)
          }
        }, 600000) // 10 minutes
      }

      emailAttachments.value = []
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description:
          'E-Mail konnte nicht gesendet werden: ' +
          (error.body?.detail || error.message || JSON.stringify(error)),
        variant: 'destructive'
      })
    } finally {
      isSendingEmail.value = false
      // Refresh case data and emails
      await refreshCaseRecord()
      await fetchEmails(false)

      // Ensure accordion is expanded (only set if not already open to avoid flicker)
      const wasOpen = emailHistoryAccordionValue.value === 'email-history'
      if (!wasOpen) {
        emailHistoryAccordionValue.value = 'email-history'
        await nextTick()
        setTimeout(() => {
          scrollToEmailHistory()
        }, 300)
      } else {
        await nextTick()
        scrollToEmailHistory()
      }
    }
  }

  function scrollToEmailHistory() {
    const emailHistoryElement = document.getElementById('email-history')
    if (emailHistoryElement) {
      const elementPosition = emailHistoryElement.getBoundingClientRect().top + window.scrollY
      const offsetPosition = elementPosition - 20 // 20px offset from top

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }

  async function fetchEmails(showLoading = true) {
    if (showLoading) {
      isLoadingEmails.value = true
    }
    try {
      const caseUuid = caseRecord.value?.uuid
      if (caseUuid) {
        const response = await EmailsService.emailsList({
          _case: caseUuid,
          limit: 50
        })
        emails.value = response.results as CustomerEmail[]

        // Clean up pending emails that now have delivery events
        const currentEmailUuids = new Set(emails.value.map((email) => email.uuid))
        const pendingToRemove: string[] = []

        pendingEmailUuids.value.forEach((uuid) => {
          const email = emails.value.find((e) => e.uuid === uuid)
          // Remove from pending if email now has a delivery event
          if (email && email.relevant_delivery_event) {
            pendingToRemove.push(uuid)
          }
        })

        pendingToRemove.forEach((uuid) => {
          pendingEmailUuids.value.delete(uuid)
          console.log('🧹 Cleaned up pending email with delivery event:', uuid)
        })
      }
    } catch (error) {
      console.error('Error fetching emails:', error)
    } finally {
      isLoadingEmails.value = false
    }
  }

  // Helper function to get initials from a name
  function getInitials(name: string): string {
    if (!name) return ''
    const words = name.split(' ')
    const initials = words.map((word) => word.charAt(0).toUpperCase())
    return initials.join('')
  }

  // Computed property for template options
  const templateOptions = computed(() => {
    if (!emailTemplates.value.length) {
      return [{ value: '', title: t('selectTemplate') }]
    }

    return emailTemplates.value.map((template) => ({
      value: template.uuid,
      title: template.name
    }))
  })

  const getDocumentBadgeColor = (documentType: string | DocumentTypeEnum | DocumentTypeIdEnum) => {
    switch (documentType) {
      case DocumentTypeEnum.FRAGEBOGEN:
        return 'bg-[#17a2b8]'
      case DocumentTypeIdEnum.QUESTIONNAIRE:
        return 'bg-[#17a2b8]'

      case DocumentTypeEnum.DOKUMENTENVORLAGE:
        return 'bg-[#dc3545]'
      case DocumentTypeIdEnum.PDF_TEMPLATE:
        return 'bg-[#dc3545]'
      case DocumentTypeIdEnum.PDF_FORM_SUBMISSION:
        return 'bg-[#dc3545]'

      case DocumentTypeEnum.UPLOAD:
        return 'bg-black'
      default:
        return 'bg-[#84cc16]'
    }
  }

  // Open document preview in PDF viewer or Office preview
  const previewDocument = async (document: any) => {
    try {
      // Handle PDF form submissions differently - they have a direct download URL
      if (document.document_type === DocumentTypeIdEnum.PDF_FORM_SUBMISSION) {
        const pdfUrl = document.download?.url
        if (pdfUrl) {
          currentPdfUrl.value = pdfUrl
          currentPdfFilename.value = document.filename
          currentDocumentUuid.value = document.uuid
          showPdfViewer.value = true
        } else {
          toast({
            variant: 'destructive',
            title: 'Fehler',
            description: 'PDF URL not available for this submission.'
          })
        }
        return
      }

      const response = await DocumentsService.documentsRetrieve({ uuid: document.uuid })
      if (response.url) {
        // Check if it's an Office file that should use the Office preview
        if (isOfficeFile(document)) {
          currentOfficeDocument.value = response as PdfDocument
          showOfficePreview.value = true
        } else {
          // Use NewPdfViewer if feature flag is enabled, otherwise use old PDF viewer
          if (newPdfEditorEnabled.isEnabled.value) {
            // Pass the full document object with all necessary properties for persistence
            selectedDocument.value = {
              ...response,
              id: document.uuid,
              uuid: document.uuid,
              title: document.filename,
              filename: document.filename,
              file: response.url,
              url: response.url,
              current_pdf_url: response.current_pdf_url || response.url,
              original_pdf_url: response.original_pdf_url || response.url,
              is_pdf_modified: response.is_pdf_modified || false,
              organisation: document.organisation,
              created_at: document.created_at,
              modified_at: document.modified_at,
              tag: document.tag,
              description: document.description,
              due_at: document.due_at,
              document_name: document.document_name
            } as any
            showPdfEditor.value = true
          } else {
            // Use old PDF viewer for PDFs and other documents
            currentPdfUrl.value = response.url
            currentPdfFilename.value = document.filename
            currentDocumentUuid.value = document.uuid
            showPdfViewer.value = true
          }
        }
      } else {
        toast({
          variant: 'destructive',
          title: 'Fehler',
          description: 'Cannot open document.'
        })
      }
    } catch (error) {
      console.error('Error opening document:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Error opening document: ${error}`
      })
    }
  }

  // Open document in new tab
  const downloadDocument = async (document: any) => {
    try {
      // Handle PDF form submissions differently - they have a direct download URL
      if (document.document_type === DocumentTypeIdEnum.PDF_FORM_SUBMISSION) {
        const pdfUrl = document.download?.url
        if (pdfUrl) {
          window.open(pdfUrl, '_blank')
        } else {
          toast({
            variant: 'destructive',
            title: 'Fehler',
            description: 'PDF URL not available for this submission.'
          })
        }
        return
      }

      const response = await DocumentsService.documentsRetrieve({ uuid: document.uuid })
      if (response.url) {
        window.open(response.url, '_blank')
      } else {
        toast({
          variant: 'destructive',
          title: 'Fehler',
          description: 'Cannot open document.'
        })
      }
    } catch (error: any) {
      console.log(error)
      if (error.status === 404) {
        toast({
          variant: 'destructive',
          title: 'Fehler',
          description: 'Es existieren keine Dokumente für diese Anfrage.'
        })
        return
      }
      console.error('Error opening document in new tab:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Error opening document: ${error}`
      })
    }
  }

  const isOfficeFile = (document: any) => {
    return officeEditingFlag.isEnabled.value && isOfficeFileUtil(document)
  }

  const isEditableDocument = (document: { document_type: string; filename?: string }) => {
    return officeEditingFlag.isEnabled.value && isEditableDocumentUtil(document)
  }

  const openOfficeFile = (document: any) => {
    if (!officeEditingFlag.isEnabled.value) {
      previewDocument(document)
      return
    }
    openOfficeEditor(document)
  }

  const previewOfficeDocument = async (document: any) => {
    try {
      const response = await DocumentsService.documentsRetrieve({ uuid: document.uuid })
      if (response) {
        currentOfficeDocument.value = response as PdfDocument
        showOfficePreview.value = true
      } else {
        toast({
          variant: 'destructive',
          title: 'Fehler',
          description: 'Cannot open document.'
        })
      }
    } catch (error) {
      console.error('Error opening Office document preview:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Error opening document: ${error}`
      })
    }
  }

  const openDocument = (document: RequestedDocuments) => {
    if (document.document_type === DocumentTypeEnum.FRAGEBOGEN) {
      openQuestionnaireSubmission(document)
      return
    }
    if (isOfficeFile(document)) {
      openOfficeFile(document)
    } else {
      previewDocument(document)
    }
  }

  const openQuestionnaireSubmission = (questionnaire: any) => {
    if (questionnaire && questionnaire.document_type === DocumentTypeEnum.FRAGEBOGEN) {
      selectedQuestionnaireSubmission.value = questionnaire
      showQuestionnaireSubmissionModal.value = true
    }
  }

  // Handle manual close of questionnaire
  const handleCloseQuestionnaire = async (questionnaire: any) => {
    try {
      await QuestionnaireSubmissionsService.questionnaireSubmissionsPartialUpdate({
        uuid: questionnaire.uuid,
        requestBody: {
          closed: true
        }
      })

      // Refresh the document lists to update the requested/received documents
      await refreshCaseRecord()

      toast({
        title: 'Questionnaire closed',
        description: 'The questionnaire has been closed successfully',
        variant: 'default'
      })
    } catch (e: any) {
      toast({
        title: 'Error closing questionnaire',
        description: `${e}`,
        variant: 'destructive'
      })
    }
  }

  const openEmailInNewTab = (email: CustomerEmail) => {
    if (!email || !email.pdf_url) return
    window.open(email.pdf_url, '_blank')
  }

  // Delivery event helper functions
  const getDeliveryEventStyle = (event: string) => {
    switch (event) {
      case 'SENT':
        return 'bg-blue-100 text-blue-800'
      case 'DELIVERED':
        return 'bg-green-100 text-green-800'
      case 'OPENED':
        return 'bg-purple-100 text-purple-800'
      case 'CLICKED':
        return 'bg-indigo-100 text-indigo-800'
      case 'HARD_BOUNCED':
      case 'SOFT_BOUNCED':
        return 'bg-red-100 text-red-800'
      case 'BLOCKED':
      case 'SPAM':
        return 'bg-orange-100 text-orange-800'
      case 'UNSUBSCRIBED':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getDeliveryEventIcon = (event: string) => {
    switch (event) {
      case 'SENT':
        return MailIcon
      case 'DELIVERED':
        return CheckIcon
      case 'OPENED':
        return EyeIcon
      case 'CLICKED':
        return MousePointerClickIcon
      case 'HARD_BOUNCED':
      case 'SOFT_BOUNCED':
        return XCircleIcon
      case 'BLOCKED':
        return ShieldXIcon
      case 'SPAM':
        return AlertTriangleIcon
      case 'UNSUBSCRIBED':
        return XCircleIcon
      default:
        return MailIcon
    }
  }

  const getDeliveryEventLabel = (event: string) => {
    switch (event) {
      case 'SENT':
        return t('deliveryEventSent')
      case 'DELIVERED':
        return t('deliveryEventDelivered')
      case 'OPENED':
        return t('deliveryEventOpened')
      case 'CLICKED':
        return t('deliveryEventClicked')
      case 'HARD_BOUNCED':
        return t('deliveryEventHardBounced')
      case 'SOFT_BOUNCED':
        return t('deliveryEventSoftBounced')
      case 'BLOCKED':
        return t('deliveryEventBlocked')
      case 'SPAM':
        return t('deliveryEventSpam')
      case 'UNSUBSCRIBED':
        return t('deliveryEventUnsubscribed')
      default:
        return t('deliveryEventUnknown')
    }
  }
  const summarizeInquiryWithAI = async () => {
    try {
      const caseUuid = caseRecord.value?.uuid
      if (!caseUuid) {
        throw new Error('Case UUID is missing')
      }

      isGeneratingSummary.value = true

      const response = await CasesService.casesAiSummaryCreate({
        uuid: caseUuid
      })

      inquirySummary.value = response
    } catch (error) {
      console.error('Error summarizing inquiry:', error)
      toast({
        title: 'Fehler',
        description: 'Fehler beim Zusammenfassen der Anfrage',
        variant: 'destructive'
      })
    } finally {
      isGeneratingSummary.value = false
    }
  }

  const openDeliveryStatusModal = (email: CustomerEmail) => {
    selectedEmailForDeliveryStatus.value = email
    showDeliveryStatusModal.value = true
  }

  const updateLastSeenAt = async () => {
    try {
      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value?.uuid as string,
        requestBody: {
          last_activity_seen_at: new Date().toISOString()
        }
      })

      // Refresh the case data to get the updated activities
      await refreshCaseRecord()
    } catch (error) {
      console.error('Failed to update last activity seen at:', error)
    }
  }

  // Initialize attachments from email template
  const initializeAttachmentsFromTemplate = (template: CustomerEmailTemplate) => {
    if (!template) return

    const attachments: AttachedDocument[] = []

    // Add forms from template
    if (template.forms && template.forms.length > 0) {
      template.forms.forEach((form) => {
        attachments.push({
          documentType: {
            id: DocumentTypeIdEnum.PDF_TEMPLATE,
            name: 'Dokumentenvorlage'
          },
          documentProperties: {
            id: form.id,
            organisation: form.organisation,
            created_at: form.created_at,
            modified_at: form.modified_at,
            tag: form.tag,
            title: form.title,
            description: form.description,
            file: form.file,
            due_at: form.due_at,
            document_name: form.document_name,
            annotationData: {},
            signatures: {}
          } as FormDocumentProperties
        })
      })

      // Load PDFs using the batch loader
      newPdfStore
        .batchLoadPdfs(template.forms)
        .then((results) => {
          attachments.forEach((attachment: AttachedDocument) => {
            if (attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
              const formProps = attachment.documentProperties as FormDocumentProperties
              if (results[formProps.id] === true) {
                const currentAnnotations = newPdfStore.getAnnotations(formProps.id)

                formProps.annotationData = [...currentAnnotations]

                const formSignatures = signatureStore.getFormSignatures(formProps.id)
                formProps.signatures = formSignatures
              }
            }
          })
        })
        .catch((error) => {
          console.error('Error in batch loading PDFs:', error)
        })
    }

    // Add questionnaires from template
    if (template.questionnaires && template.questionnaires.length > 0) {
      template.questionnaires.forEach((questionnaire) => {
        attachments.push({
          documentType: {
            id: DocumentTypeIdEnum.QUESTIONNAIRE,
            name: 'Fragebogen'
          },
          documentProperties: {
            ...questionnaire,
            id: questionnaire.uuid
          } as QuestionnaireDocumentProperties
        })
      })
    }

    // Update the attachments
    emailAttachments.value = attachments
  }

  const newPdfEditorEnabled = useFeatureFlag(FlagEnum.NEW_PDF_EDITOR)

  // Feature flag for agent action widget
  const isAgentActionWidgetEnabled = ref(false)
  const aiSettings = ref(null)

  // Check feature flag and AI settings on mount
  const checkAgentActionWidgetFlag = async () => {
    const featureFlagEnabled = await useFeatureFlag(FlagEnum.AGENTIC_WORKFLOWS)

    const response = await AiSettingsService.aiSettingsRetrieve({ uuid: 'current' })
    aiSettings.value = response

    // Enable widget only if both feature flag is enabled AND AI workflows are enabled
    isAgentActionWidgetEnabled.value = featureFlagEnabled && aiSettings.value.ai_workflows_enabled
  }

  const attachmentHasUnfilledRequiredFields = (attachment: AttachedDocument): boolean => {
    if (attachment.documentType.id !== DocumentTypeIdEnum.PDF_TEMPLATE) {
      return false
    }

    const formProps = attachment.documentProperties as FormDocumentProperties
    const pdfState = newPdfStore.selectedPdfs.get(formProps.id)
    return pdfState?.hasLawfirmUnfilledRequiredFields ?? false
  }

  const missingSignaturesMap = ref<Record<string, boolean>>({})

  const hasMissingSignatures = computed(() => {
    const pdfAttachments = emailAttachments.value.filter(
      (attachment) => attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE
    )

    return pdfAttachments.some((attachment) => {
      const formProps = attachment.documentProperties as FormDocumentProperties
      return newPdfStore.hasLawfirmMissingSignatures(formProps.id)
    })
  })

  const attachmentHasMissingSignatures = (attachment: AttachedDocument): boolean => {
    if (attachment.documentType.id !== DocumentTypeIdEnum.PDF_TEMPLATE) {
      return false
    }

    const formProps = attachment.documentProperties as FormDocumentProperties
    return newPdfStore.hasLawfirmMissingSignatures(formProps.id)
  }

  // Function to update signature validation for all attachments
  const updateSignatureValidation = async () => {
    const pdfAttachments = emailAttachments.value.filter(
      (attachment) => attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE
    )

    const promises = pdfAttachments.map(async (attachment) => {
      const formProps = attachment.documentProperties as FormDocumentProperties
      const hasMissing = await signatureStore.hasMissingSignatures(formProps.id)
      return { formId: formProps.id, hasMissing }
    })

    const results = await Promise.all(promises)
    const newMap: Record<string, boolean> = {}
    results.forEach(({ formId, hasMissing }) => {
      newMap[formId] = hasMissing
    })

    missingSignaturesMap.value = newMap
  }

  // Watch for selectedEmailTemplate changes to initialize attachments
  watch(
    () => selectedEmailTemplate.value,
    (template) => {
      if (template) {
        initializeAttachmentsFromTemplate(template)
        updateSignatureValidation()
      }
    },
    { immediate: true }
  )

  // Handle WebSocket messages for email delivery events
  watch(
    () => caseStore.caseWebSocket.lastMessage,
    async (message) => {
      if (!message || !caseRecord.value?.uuid) return
      console.log('📧 Email Delivery Event received:', message)
      // Handle email delivery event messages
      if (
        message.type === 'case_email_delivery_event' &&
        message.data?.case_uuid === caseRecord.value.uuid &&
        message.data?.email_uuid
      ) {
        console.log('📧 Email Delivery Event received:', {
          emailUuid: message.data.email_uuid,
          deliveryEvent: message.data.delivery_event_event,
          caseUuid: message.data.case_uuid
        })

        // Remove email from pending state since we received a delivery event
        if (message.data.email_uuid && pendingEmailUuids.value.has(message.data.email_uuid)) {
          pendingEmailUuids.value.delete(message.data.email_uuid)
          console.log('⏰ Removed email from pending state:', message.data.email_uuid)
        }

        // Refetch all emails to get the latest delivery status
        // Note: We refetch all emails because the API doesn't support retrieving individual emails by UUID
        try {
          await fetchEmails()
          console.log('✅ Email list refreshed after delivery event:', {
            emailUuid: message.data.email_uuid,
            deliveryEvent: message.data.delivery_event_event
          })
        } catch (error) {
          console.error('❌ Error refreshing emails after delivery event:', error)
        }
      }
    },
    { immediate: false }
  )

  // Watch for changes in caseRecord data to set default values
  watch(
    () => caseRecord.value,
    (caseRecord) => {
      if (caseRecord) {
        // Set default legal field
        if (caseRecord.data?.law?.field) {
          selectedLawField.value = caseRecord.data.law.field
        }

        // Set default legal topic
        if (caseRecord.data?.law?.topic) {
          selectedLawTopic.value = caseRecord.data.law.topic
        }

        // Set default clerk
        if (caseRecord.clerk) {
          selectedClerk.value = caseRecord.clerk.uuid
        } else {
          selectedClerk.value = ''
        }

        if (caseRecord.data) {
          const client = caseRecord.data.client
          if (client) {
            newPdfStore.setPrefillData({
              client_first_name: client.first_name ?? '',
              client_last_name: client.last_name ?? '',
              client_email: client.email ?? '',
              client_phone: client.phone ?? '',
              probably_client_city: client.city ?? '',
              probably_client_zip: client.zip ?? '',
              probably_client_street: client.street ?? '',
              probably_client_street_number: client.street_number ?? '',
              probably_client_date_of_birth: client.date_of_birth ?? ''
            })
          }
        }
      }
    },
    { immediate: true }
  )

  watch(
    () => signatureStore.signatures,
    () => {
      updateSignatureValidation()
    },
    { deep: true }
  )

  onMounted(() => {
    Promise.all([
      caseStore.loadCase(router.currentRoute.value.params.uuid as string),
      userStore.fetchUser(),
      fetchLawFields(),
      fetchEmailTemplates(),
      refreshCaseRecord(),
      newPdfEditorEnabled.checkFlag(),
      checkAgentActionWidgetFlag(),
      newPdfEditorEnabled.checkFlag(),
      officeEditingFlag.checkFlag()
    ])
      .then(() => {
        updateLastSeenAt()
        fetchEmails()
        updateSignatureValidation()
      })
      .catch((error) => {
        console.error('Error loading case data:', error)
      })
  })

  // Clean up pending emails on unmount
  onUnmounted(() => {
    pendingEmailUuids.value.clear()
  })
</script>

<template>
  <SpaCompatComponent>
    <div
      v-if="caseRecord"
      class="container px-0"
    >
      <LegacyCaseHeader />
      <div class="row pb-3">
        <AutoSavingForm
          :debounceDelay="1500"
          :throttleInterval="2000"
          @save="handleCaseInfoSave"
        >
          <div class="flex flex-row flex-wrap gap-3">
            <div class="mb-3 flex-1">
              <div class="form-group">
                <SelectField
                  v-model="selectedLawField"
                  name="lawField"
                  :options="lawFieldOptions"
                  :label="t('lawField')"
                />
              </div>
            </div>
            <div class="mb-3 flex-1">
              <div class="form-group">
                <SelectField
                  v-model="selectedLawTopic"
                  name="lawTopic"
                  :options="lawTopicOptions"
                  :label="t('lawTopic')"
                />
              </div>
            </div>
            <div class="mb-3 flex-1">
              <div class="form-group">
                <SelectField
                  v-model="selectedClerk"
                  name="clerk"
                  :options="clerkOptions"
                  :label="t('clerk')"
                />
              </div>
            </div>
          </div>
        </AutoSavingForm>
      </div>
      <div class="row pb-3">
        <div class="col-12 col-lg-4 mt-lg-0">
          <div
            class="card"
            data-cy="clientCard"
          >
            <h3
              id="headingTwo"
              class="accordion-button mb-0 bg-white"
              aria-controls="clientCard"
              aria-expanded="true"
              data-bs-target="#clientCard"
              data-bs-toggle="collapse"
              data-cy="clientCardHeader"
            >
              {{ t('client') }}
            </h3>
            <div
              id="clientCard"
              class="card-body show collapse"
              aria-labelledby="headingOne"
            >
              <LegacyEntityForm fieldPrefix="client" />
            </div>
          </div>
          <div
            class="card mt-3"
            data-cy="opponentCard"
          >
            <h3
              id="headingThree"
              class="accordion-button collapsed mb-0 bg-white"
              aria-controls="opponentCard"
              aria-expanded="false"
              data-bs-target="#opponentCard"
              data-bs-toggle="collapse"
              data-cy="opponentCardHeader"
            >
              {{ t('opponent') }}
            </h3>
            <div
              id="opponentCard"
              class="card-body collapse"
              aria-labelledby="headingThree"
            >
              <LegacyEntityForm fieldPrefix="opponent" />
            </div>
          </div>
          <div
            class="card mt-3"
            data-cy="legalInsuranceCard"
          >
            <div class="card-body">
              <div>
                <AutoSavingForm
                  :debounceDelay="1500"
                  :throttleInterval="2000"
                  @save="handleInsuranceInfoSave"
                >
                  <div
                    class="form-group mb-3 flex flex-col"
                    style="gap: 0.5rem"
                  >
                    <CheckboxField
                      v-model="hasLegalInsurance"
                      name="has_legal_insurance"
                      :label="t('legalInsurance')"
                    />
                    <InputField
                      v-if="hasLegalInsurance"
                      v-model="legalInsuranceName"
                      name="legal_insurance_name"
                      :label="t('legalInsuranceName')"
                    />
                    <InputField
                      v-if="hasLegalInsurance"
                      v-model="legalInsuranceNumber"
                      name="legal_insurance_number"
                      :label="t('legalInsuranceNumber')"
                    />
                  </div>
                </AutoSavingForm>
                <!-- Additional client insurance content would go here -->
              </div>
            </div>
          </div>
          <div
            class="card mt-3"
            data-cy="internalNotesCard"
          >
            <div class="card-body">
              <AutoSavingForm
                ref="notesFormRef"
                :debounceDelay="1500"
                :throttleInterval="2000"
                @save="handleNotesSave"
              >
                <div class="mb-3">
                  <div class="form-group">
                    <TextAreaField
                      v-model="internalNotes"
                      name="notes"
                      :label="t('internalNotes')"
                      :placeholder="t('notesPlaceholder')"
                      rows="10"
                      @focus="() => notesFormRef?.handleFieldTouch('notes')"
                    />
                  </div>
                </div>
              </AutoSavingForm>
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-8">
          <AgentActionWidget
            v-if="caseRecord && isAgentActionWidgetEnabled"
            :case-uuid="caseRecord.uuid"
          />
          <div class="flex-column flex gap-3">
            <!-- Inhalt der Anfrage Card -->
            <div
              class="card mt-3 lg:mt-0"
              data-cy="inquiryCard"
            >
              <div class="row">
                <div class="col-12">
                  <div class="card-header block">
                    <div class="row">
                      <div class="col-6">
                        <h3 class="card-title pt-1">{{ t('inquiry') }}</h3>
                      </div>
                      <div class="col-6">
                        <Button
                          v-if="
                            caseRecord.org.ai_enabled && !inquirySummary && !isGeneratingSummary
                          "
                          class="gradient-hover float-end"
                          variant="secondary"
                          @click="summarizeInquiryWithAI"
                        >
                          🪄
                          {{ t('summarizeWithAI') }}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <div
                  v-if="caseRecord?.inquiry"
                  v-html="caseRecord.inquiry"
                ></div>
              </div>
            </div>
            <!-- Summary of Inquiry -->
            <div
              v-if="inquirySummary || isGeneratingSummary"
              class="card mt-3 lg:mt-0"
              data-cy="inquirySummaryCard"
            >
              <div class="row">
                <div class="col-12">
                  <div class="card-header block">
                    <div class="row">
                      <div class="col-6">
                        <h3 class="gradient card-title pt-1">{{ t('summaryOfInquiry') }}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <div class="space-y-3">
                  <div
                    v-if="isGeneratingSummary"
                    class="flex justify-center"
                  >
                    <div
                      class="spinner-border text-primary"
                      role="status"
                    >
                      <span class="visually-hidden">Loading...</span>
                    </div>
                  </div>
                  <div
                    class="leading-loose"
                    v-html="inquirySummary"
                  ></div>
                </div>
              </div>
            </div>
            <!-- Termine Card -->
            <div
              v-if="user?.org?.enable_appointments"
              class="card"
              data-cy="appointmentsCard"
            >
              <div class="row">
                <div class="col-12">
                  <div class="card-header block">
                    <div class="row">
                      <div class="col-6">
                        <h3 class="card-title pt-1">{{ t('appointments') }}</h3>
                      </div>
                      <div class="col-6">
                        <span class="float-end">
                          <Button
                            variant="secondary"
                            data-cy="createAppointmentBtn"
                            @click="isAppointmentCreateModalOpen = true"
                          >
                            <CalendarPlusIcon class="me-2 h-4 w-4" />
                            {{ t('createAppointment') }}
                          </Button>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="card-body p-0">
                    <div class="table-responsive mb-0">
                      <table class="table-vcenter card-table table">
                        <tbody>
                          <!-- Appointment Proposals Section -->
                          <template
                            v-for="proposal in caseRecord?.appointment_proposals"
                            :key="proposal.uuid"
                          >
                            <tr
                              v-if="!proposal.booked"
                              class="bg-gray-50"
                              data-cy="appointmentProposalRow"
                            >
                              <td class="w-1/3">
                                <div class="flex items-center py-1">
                                  <div class="flex-1">
                                    <div class="flex flex-col text-xs font-medium">
                                      <div
                                        class="f"
                                        data-cy="proposalStatus"
                                      >
                                        {{ t('bookingLinkSent') }}
                                      </div>
                                      <div data-cy="proposalDate">
                                        <PencilIcon class="mr-1 inline-block h-3 w-3" />
                                        {{ proposal.start ? formatDate(proposal.start) : '' }}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td class="w-1/3">
                                <div class="flex items-center py-1">
                                  <div class="flex-1">
                                    <div
                                      class="text-xs text-black"
                                      data-cy="proposalCalendar"
                                    >
                                      <template v-if="proposal.calendar">
                                        <UserIcon class="mr-1 inline-block h-3.5 w-3.5" />
                                        {{ proposal.calendar.name }}
                                      </template>
                                    </div>
                                    <div
                                      class="whitespace-nowrap text-xs font-medium"
                                      data-cy="proposalService"
                                    >
                                      <InfoIcon class="mr-1 inline-block h-3.5 w-3.5" />
                                      {{ proposal.service?.name || t('noService') }}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td class="w-1/3">
                                <div class="flex items-center py-1">
                                  <div class="flex flex-1 items-center justify-between gap-3">
                                    <div
                                      class="font-medium"
                                      data-cy="proposalLocationType"
                                    >
                                      <MapPinIcon class="mr-1 inline-block h-4 w-4" />
                                      <span v-if="proposal.location_type === 'TELEPHONE'">
                                        {{ t('telephoneAppointment') || 'Telefon-Termin' }}
                                      </span>
                                      <span v-if="proposal.location_type === 'ONLINE'">
                                        {{ t('online') || 'Online-Termin' }}
                                      </span>
                                      <span v-if="proposal.location_type === 'IN_PERSON'">
                                        {{ t('inPerson') || 'Termin vor Ort' }}
                                      </span>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      class="mr-2 text-xs text-red-500 hover:bg-red-500 hover:text-white"
                                      @click="deleteAppointmentProposal(proposal)"
                                    >
                                      {{ t('delete') || 'Löschen' }}
                                    </Button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          </template>

                          <!-- Appointments Section -->
                          <tr
                            v-for="appointment in caseRecord?.appointments"
                            :key="appointment.uuid"
                            class="hover:bg-gray-50"
                            data-cy="appointmentRow"
                          >
                            <td class="w-1/3">
                              <div class="flex items-center py-1">
                                <div class="flex-1">
                                  <div class="text-[15px] font-medium">
                                    <Calendar1Icon class="mr-1 inline-block h-4 w-4" />
                                    {{ formatDate(appointment.start) }}
                                  </div>
                                  <div class="text-[15px] font-medium">
                                    <Clock5Icon class="mr-1 inline-block h-4 w-4" />
                                    {{ formatTime(appointment.start, appointment.end) }}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td class="w-1/3">
                              <div class="flex items-center py-1">
                                <div class="flex-1">
                                  <div
                                    class="text-[15px] font-medium"
                                    data-cy="appointmentCalendar"
                                  >
                                    <UserIcon class="mr-1 inline-block h-4 w-4" />
                                    {{ appointment.calendar?.name || t('unknown') }}
                                  </div>
                                  <div
                                    class="text-[15px] font-medium"
                                    data-cy="appointmentService"
                                  >
                                    <InfoIcon class="mr-1 inline-block h-4 w-4" />
                                    {{ appointment.service?.name || t('noService') }}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td class="w-1/3">
                              <div class="flex items-center py-1">
                                <div class="flex flex-1 items-center justify-between gap-3">
                                  <div>
                                    <div
                                      class="text-sm font-medium"
                                      data-cy="appointmentLocationType"
                                    >
                                      <MapPinIcon class="mr-1 inline-block h-4 w-4" />
                                      {{
                                        appointment.location_types === 'ONLINE'
                                          ? t('online')
                                          : appointment.location_types === 'TELEPHONE'
                                            ? t('telephoneAppointment')
                                            : t('inPerson')
                                      }}
                                    </div>
                                    <div class="mt-1 flex items-center gap-1">
                                      <CalendarIcon class="h-4 w-4" />
                                      <span
                                        class="rounded-sm px-1 py-[3px] text-[10px] font-semibold text-white"
                                        :class="
                                          appointment.status === 'CONFIRMED'
                                            ? 'bg-[#009b72]'
                                            : appointment.status === 'CANCELLED'
                                              ? 'bg-[#dc3545]'
                                              : 'bg-gray-500'
                                        "
                                        data-cy="appointmentStatus"
                                      >
                                        {{ getAppointmentStatusText(appointment) }}
                                      </span>
                                    </div>
                                  </div>
                                  <div
                                    v-if="new Date(appointment.start) > new Date()"
                                    class="flex gap-2"
                                  >
                                    <Button
                                      v-if="appointment.status === AppointmentStatusEnum.TENTATIVE"
                                      variant="secondary"
                                      class="text-green-600 hover:bg-green-600 hover:text-white"
                                      @click="confirmAppointmentBooking(appointment)"
                                    >
                                      {{ t('confirmAppointment') }}
                                    </Button>
                                    <Button
                                      v-if="appointment.status === AppointmentStatusEnum.TENTATIVE"
                                      variant="secondary"
                                      class="text-red-500 hover:bg-red-500 hover:text-white"
                                      @click="cancelAppointmentBooking(appointment)"
                                    >
                                      <CircleXIcon class="h-4 w-4" />
                                    </Button>
                                    <Button
                                      v-if="appointment.status === AppointmentStatusEnum.CONFIRMED"
                                      variant="secondary"
                                      class="text-red-500 hover:bg-red-500 hover:text-white"
                                      @click="cancelAppointmentBooking(appointment)"
                                    >
                                      {{ t('cancelAppointment') }}
                                    </Button>
                                    <Button
                                      v-if="appointment.status === AppointmentStatusEnum.CANCELLED"
                                      variant="secondary"
                                      class="text-red-500 hover:bg-red-500 hover:text-white"
                                      @click="deleteAppointmentBooking(appointment)"
                                    >
                                      {{ t('delete') }}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                          <tr
                            v-if="
                              (!caseRecord?.appointments ||
                                caseRecord?.appointments.length === 0) &&
                              (!caseRecord?.appointment_proposals ||
                                caseRecord.appointment_proposals.length === 0)
                            "
                          >
                            <td
                              colspan="4"
                              class="py-4 text-center text-gray-500"
                              data-cy="noAppointmentsHint"
                            >
                              {{ t('noAppointments') || 'Keine Termine vorhanden' }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Received Documents Card -->
            <div
              id="received-documents"
              class="card"
              style="z-index: 0"
            >
              <Accordion
                type="single"
                collapsible
                class="w-full"
                defaultValue="received-documents"
              >
                <AccordionItem value="received-documents">
                  <AccordionHeader class="card-header flex grow">
                    <AccordionTrigger class="w-full py-0 hover:no-underline">
                      <div class="flex w-full items-center justify-between">
                        <h3 class="card-title pe-2">
                          {{ t('receivedDocuments') }}
                          <span class="badge bg-danger">{{ receivedDocuments.length }}</span>
                        </h3>
                      </div>
                      <div class="flex items-center">
                        <a
                          v-if="newPdfEditorEnabled.isEnabled.value"
                          :href="`/app/cases/${caseRecord?.uuid}/documents`"
                          class="documents-edit-link me-3"
                        >
                          <span class="link-text">Dokumente bearbeiten</span>
                          <span class="neu-badge">NEU</span>
                        </a>
                        <DropdownMenu>
                          <DropdownMenuTrigger as-child>
                            <Button
                              variant="secondary"
                              class="me-2 flex h-8 w-12"
                              @click.stop
                            >
                              <EllipsisVerticalIcon class="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            class="w-[180px]"
                            :align="'end'"
                          >
                            <DropdownMenuItem
                              class="cursor-pointer"
                              @click="isDocumentUploadModalOpen = true"
                            >
                              {{ t('addDocument') }}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </AccordionTrigger>
                  </AccordionHeader>
                  <AccordionContent class="px-4 pb-4">
                    <!-- No Documents State -->
                    <div
                      v-if="receivedDocuments.length === 0"
                      class="p-3 pb-0"
                      data-cy="noDocsReceivedHint"
                    >
                      <em class="small">{{ t('noDocumentsReceived') }}</em>
                    </div>

                    <!-- Documents List -->
                    <table
                      v-else
                      class="table-vcenter card-table table table-fixed"
                    >
                      <tbody>
                        <tr>
                          <td class="w-1/2">
                            <div class="flex items-center py-1">
                              {{ t('sortBy') || 'Sortieren nach:' }}
                            </div>
                          </td>
                          <td></td>
                          <td class="w-1/4">
                            <SelectField
                              v-model="receivedDocsSortBy"
                              name="sortBy"
                              :options="[
                                { value: 'name', title: t('name') },
                                { value: 'date', title: t('date') }
                              ]"
                            ></SelectField>
                          </td>
                        </tr>
                        <tr
                          v-for="document in receivedDocuments"
                          :key="document.uuid"
                          class="hover:bg-gray-50"
                          data-cy="receivedDocumentRow"
                        >
                          <td class="w-1/2">
                            <div class="flex items-center py-1">
                              <div
                                class="avatar me-2 h-10 w-10 overflow-hidden rounded border border-gray-100 bg-white"
                              >
                                <img
                                  v-if="getThumbnailUrl(document)"
                                  :src="getThumbnailUrl(document)"
                                  alt="Document thumbnail"
                                  class="h-full w-full object-cover"
                                />
                                <div
                                  v-else
                                  class="flex h-full w-full items-center justify-center bg-gray-50"
                                >
                                  <FileIcon class="h-5 w-5 text-gray-400" />
                                </div>
                              </div>
                              <div class="min-w-0 flex-1">
                                <div
                                  :class="[
                                    'truncate font-medium',
                                    isOfficeFile(document)
                                      ? 'cursor-pointer text-blue-600 hover:text-blue-800 hover:underline'
                                      : 'cursor-pointer'
                                  ]"
                                  :title="
                                    isOfficeFile(document)
                                      ? `${document.filename} - Click to edit in office editor`
                                      : document.filename
                                  "
                                  data-cy="receivedDocumentTitle"
                                  @click="openDocument(document)"
                                >
                                  {{ document.filename.replace('.pdf', '') }}
                                </div>
                                <div class="truncate text-sm text-gray-500">
                                  {{ document.filename }}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td class="w-1/4">
                            <div class="flex flex-col">
                              <div
                                :class="`badge w-fit ${getDocumentBadgeColor(document.document_type)}`"
                                data-cy="receivedDocumentStatus"
                              >
                                {{
                                  document.document_type === DocumentTypeIdEnum.PDF_FORM_SUBMISSION
                                    ? 'Dokumentenvorlage'
                                    : document.document_type === DocumentTypeEnum.UPLOAD
                                      ? (document.upload_origin as string) ||
                                        (document.document_type as string)
                                      : (document.document_type as string)
                                }}
                              </div>
                              <div class="mt-1 text-sm text-gray-500">
                                {{ formatDocumentDate(document.created_at) }}
                              </div>
                            </div>
                          </td>
                          <td class="w-1/4 text-right">
                            <div class="flex justify-end gap-2">
                              <Button
                                v-if="isEditableDocument(document)"
                                variant="secondary"
                                :title="t('edit') || 'Bearbeiten'"
                                @click="openOfficeFile(document)"
                              >
                                <PencilIcon class="h-4 w-4" />
                              </Button>
                              <Button
                                v-if="!isOfficeFile(document)"
                                variant="secondary"
                                :title="t('preview') || 'Vorschau'"
                                @click="previewDocument(document)"
                              >
                                <Eye class="h-4 w-4" />
                              </Button>
                              <Button
                                variant="secondary"
                                :title="t('download') || 'Herunterladen'"
                                @click="downloadDocument(document)"
                              >
                                <CloudDownloadIcon class="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            <!-- Requested Documents Card -->
            <div
              id="requested-documents"
              class="card"
              style="z-index: 0"
            >
              <Accordion
                type="single"
                collapsible
                class="w-full"
                defaultValue="requested-documents"
              >
                <AccordionItem value="requested-documents">
                  <AccordionHeader class="card-header flex grow">
                    <AccordionTrigger class="w-full py-0 hover:no-underline">
                      <div class="flex w-full items-center justify-between">
                        <h3 class="card-title pe-2">
                          {{ t('requestedDocuments') }}
                          <span class="badge bg-danger">{{ requestedDocuments.length }}</span>
                        </h3>
                      </div>
                      <TooltipProvider
                        v-if="
                          requestedDocuments.length && caseRecord.org.send_document_email_reminders
                        "
                        class="flex-shrink-0"
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              class="mx-4 flex items-center justify-end gap-1.5 rounded border border-purple-200 bg-purple-50 px-2 py-1
                                text-xs text-purple-700 dark:bg-purple-950 dark:text-purple-300"
                            >
                              <div class="flex items-center gap-2">
                                <span class="text-nowrap text-xs font-medium">
                                  {{ t('enableEmailReminder') }}
                                </span>
                                <template v-if="loadingChangeEmailReminderStatus">
                                  <span
                                    class="spinner-border spinner-border-sm"
                                    aria-hidden="true"
                                    role="status"
                                  ></span>
                                </template>
                                <Switch
                                  v-model:checked="isEmailReminderForOutstandingDocumentsEnabled"
                                  class="data-[state=checked]:bg-purple-700 data-[state=unchecked]:bg-gray-200"
                                  thumb-class=""
                                  :disabled="loadingChangeEmailReminderStatus"
                                  @click.stop
                                />
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent class="max-w-xs border-black bg-black text-white">
                            <p class="text-white">{{ t('enableEmailReminderTitle') }}</p>
                            <p class="whitespace-normal text-xs text-gray-300">
                              {{ t('enableEmailReminderDescription') }}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <div class="flex items-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger as-child>
                            <Button
                              variant="secondary"
                              class="me-2 flex h-8 w-12"
                              @click.stop
                            >
                              <EllipsisVerticalIcon class="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            class="w-[320px]"
                            :align="'end'"
                          >
                            <DropdownMenuItem
                              class="flex cursor-pointer items-center gap-2 whitespace-nowrap"
                              @click="handleGenerateQuestionnaireLink"
                            >
                              <div class="h-5 w-5 flex-shrink-0 rounded bg-[#17a2b8]"></div>
                              {{ t('createLinkToQuestionnaire') }}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              class="flex cursor-pointer items-center gap-2 whitespace-nowrap"
                              @click="handleGenerateUnifiedTemplateLink"
                            >
                              <div class="h-5 w-5 flex-shrink-0 rounded bg-[#dc3545]"></div>
                              {{ t('createLinkToDocumentTemplate') }}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </AccordionTrigger>
                  </AccordionHeader>
                  <AccordionContent class="px-4 pb-4">
                    <!-- Loading State -->
                    <div
                      v-if="isLoadingDocuments"
                      class="flex justify-center py-6"
                    >
                      <div
                        class="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
                      ></div>
                    </div>

                    <!-- No Documents State -->
                    <div
                      v-else-if="requestedDocuments.length === 0"
                      class="p-3 pb-0"
                      data-cy="noRequestedDocsHint"
                    >
                      <em class="small">{{ t('noRequestedDocuments') }}</em>
                    </div>

                    <!-- Documents List -->
                    <table
                      v-else
                      class="table-vcenter card-table table"
                    >
                      <tbody>
                        <tr>
                          <td class="w-1/2">
                            <div class="flex items-center py-1">
                              {{ t('sortBy') || 'Sortieren nach:' }}
                            </div>
                          </td>
                          <td></td>
                          <td class="w-1/4">
                            <SelectField
                              v-model="requestedDocsSortBy"
                              name="sortBy"
                              :options="[
                                { value: 'name', title: t('name') },
                                { value: 'date', title: t('date') }
                              ]"
                            ></SelectField>
                          </td>
                        </tr>
                        <tr
                          v-for="document in requestedDocuments"
                          :key="document.uuid"
                          class="hover:bg-gray-50"
                          data-cy="requestedDocumentRow"
                        >
                          <td class="w-1/2">
                            <div class="flex items-center py-1">
                              <div
                                class="avatar me-2 h-10 w-10 overflow-hidden rounded border border-gray-100 bg-white"
                              >
                                <img
                                  v-if="getThumbnailUrl(document)"
                                  :src="getThumbnailUrl(document)"
                                  alt="Document thumbnail"
                                  class="h-full w-full object-cover"
                                />
                                <div
                                  v-else
                                  class="flex h-full w-full items-center justify-center bg-gray-50"
                                >
                                  <FileIcon class="h-5 w-5 text-gray-400" />
                                </div>
                              </div>
                              <div class="min-w-0 flex-1">
                                <div
                                  :class="[
                                    'truncate font-medium',
                                    isOfficeFile(document)
                                      ? 'cursor-pointer text-blue-600 hover:text-blue-800 hover:underline'
                                      : 'cursor-pointer'
                                  ]"
                                  :title="
                                    isOfficeFile(document)
                                      ? `${document.filename} - Click to edit in office editor`
                                      : document.filename
                                  "
                                  data-cy="requestedDocumentTitle"
                                  @click="openDocument(document)"
                                >
                                  {{ document.filename.replace('.pdf', '') }}
                                </div>
                                <div class="truncate text-sm text-gray-500">
                                  {{ document.filename }}
                                </div>
                                <!-- Question count with progress bar for questionnaires -->
                                <div v-if="document.document_type === DocumentTypeEnum.FRAGEBOGEN">
                                  <div class="mt-2 flex items-center">
                                    <div class="w-10/12">
                                      <div
                                        class="h-2 w-full overflow-hidden rounded-full bg-gray-200"
                                      >
                                        <div
                                          class="h-full bg-blue-500"
                                          role="progressbar"
                                          :style="`width: ${(document as RequestedQuestionnaire).answered_questions_percent || 0}%`"
                                          :aria-valuenow="
                                            (document as RequestedQuestionnaire)
                                              .answered_questions_percent || 0
                                          "
                                          aria-valuemin="0"
                                          aria-valuemax="100"
                                        ></div>
                                      </div>
                                    </div>
                                    <div class="w-2/12 text-right">
                                      <span class="text-xs text-gray-500">
                                        {{
                                          (document as RequestedQuestionnaire)
                                            .answered_question_count || 0
                                        }}/{{
                                          (document as RequestedQuestionnaire).question_count || 0
                                        }}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td class="w-1/4">
                            <div class="flex flex-col">
                              <div
                                :class="`badge w-fit ${getDocumentBadgeColor(document.document_type)}`"
                                data-cy="requestedDocumentStatus"
                              >
                                {{
                                  document.document_type === DocumentTypeEnum.UPLOAD
                                    ? ((document as PdfDocument).upload_origin as string)
                                    : (document.document_type as string)
                                }}
                              </div>
                              <div class="mt-1 text-sm text-gray-500">
                                {{ formatDocumentDate(document.created_at) }}
                              </div>
                            </div>
                          </td>
                          <td class="w-1/4 text-right">
                            <div class="flex justify-end gap-2">
                              <Button
                                v-if="document.document_type === DocumentTypeEnum.FRAGEBOGEN"
                                variant="secondary"
                                :title="t('preview') || 'Ansehen'"
                                @click="openQuestionnaireSubmission(document)"
                              >
                                <Eye class="h-4 w-4" />
                              </Button>
                              <span v-else-if="isOfficeFile(document)">
                                <!--   DISABLED FOR NOW AS JWT TOKEN IS REQUIRED -->
                                <!-- <Button-->
                                <!--   variant="secondary"-->
                                <!--   :title="t('preview') || 'Vorschau'"-->
                                <!--   @click="previewOfficeDocument(document)"-->
                                <!-- >-->
                                <!--   <Eye class="h-4 w-4" />-->
                                <!-- </Button>-->
                              </span>
                              <Button
                                v-else
                                variant="secondary"
                                :title="t('preview') || 'Vorschau'"
                                @click="previewDocument(document)"
                              >
                                <Eye class="h-4 w-4" />
                              </Button>
                              <Button
                                v-if="isEditableDocument(document)"
                                variant="secondary"
                                :title="t('edit') || 'Bearbeiten'"
                                @click="openOfficeFile(document)"
                              >
                                <PencilIcon class="h-4 w-4" />
                              </Button>
                              <DropdownMenu
                                v-if="document.document_type === DocumentTypeEnum.FRAGEBOGEN"
                              >
                                <DropdownMenuTrigger>
                                  <Button variant="secondary">
                                    <EllipsisVerticalIcon class="h-4 w-5" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent :align="'end'">
                                  <DropdownMenuItem @click="handleCloseQuestionnaire(document)">
                                    {{ t('closeQuestionnaire') }}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            <div
              id="email"
              class="card"
              style="z-index: 0"
            >
              <Accordion
                type="single"
                collapsible
                class="w-full"
                defaultValue="email"
              >
                <AccordionItem value="email">
                  <AccordionHeader class="card-header flex grow">
                    <AccordionTrigger class="w-full py-0 hover:no-underline">
                      <div class="flex w-full items-center justify-between">
                        <h3 class="card-title pe-2">
                          {{ t('email') }}
                        </h3>
                      </div>
                    </AccordionTrigger>
                  </AccordionHeader>
                  <AccordionContent
                    class="px-4 pb-4"
                    :overflow="'visible'"
                  >
                    <!-- Template Selection -->
                    <div class="mb-4">
                      <div
                        class="mt-4 flex w-full items-center overflow-hidden rounded-[7px] border border-gray-200"
                      >
                        <label
                          for="email-template"
                          class="m-0 h-10 cursor-default bg-gray-50 px-3 text-sm"
                        >
                          {{ t('selectTemplate') }}
                        </label>
                        <SelectField
                          v-model="selectedTemplateId"
                          class="grow"
                          name="email-template"
                          :options="templateOptions"
                          @update:model-value="handleTemplateSelect"
                        />
                      </div>
                    </div>

                    <!-- Subject Field -->
                    <div class="relative mb-4">
                      <input
                        id="subject-input"
                        v-model="emailSubject"
                        class="w-full rounded-sm border border-gray-200 px-3 pb-2 pt-6 focus:outline-none focus:ring-2
                          focus:ring-purple-500 focus:ring-offset-2"
                        type="text"
                      />
                      <label
                        class="absolute -top-1 left-2 cursor-auto text-sm text-gray-500"
                        for="subject-input"
                      >
                        Betreff
                      </label>
                    </div>

                    <!-- Rich Text Editor Placeholder -->
                    <RichTextEditor
                      v-model="emailContent"
                      :enable-placeholder-button="true"
                      :enabled-context-aware-placeholders="['questionnaires_and_documents']"
                      :enable-text-styling-buttons="true"
                      :font-color="user?.org?.email_text_color"
                      :font-size="user?.org?.email_text_size"
                      :font-family="user?.org?.email_text_font"
                      :full-height="false"
                      class="min-h-[160px]"
                      data-cy="emailContent"
                    />

                    <!-- Attachments Section -->
                    <div
                      class="my-4 rounded-md border"
                      data-cy="emailAttachments"
                    >
                      <div class="flex items-center justify-between border-b p-3">
                        <h5 class="text-sm font-medium">
                          {{ emailAttachments.length }}
                          {{
                            t('documentsAndQuestionnaires') || 'Document Templates & Questionnaires'
                          }}
                        </h5>
                        <div
                          class="flex items-center space-x-2"
                          data-cy="attachmentsDropdown"
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              class="flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background text-sm
                                shadow-sm hover:bg-accent"
                            >
                              +
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              :align="'end'"
                              class="w-[190px]"
                            >
                              <DropdownMenuItem
                                class="flex cursor-pointer items-center gap-2"
                                data-cy="attachQuestionnaire"
                                @click="handleAttachQuestionnaire"
                              >
                                <div class="h-5 w-5 rounded bg-[#17a2b8]"></div>
                                {{ t('questionnaire') || 'Questionnaire' }}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                class="flex cursor-pointer items-center gap-2"
                                data-cy="attachDocument"
                                @click="handleAttachDocumentTemplate"
                              >
                                <div class="h-5 w-5 rounded bg-[#dc3545]"></div>
                                {{ t('documentTemplate') }}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      <!-- Attachments Table -->
                      <div class="overflow-x-auto">
                        <table class="w-full">
                          <tbody>
                            <template v-if="emailAttachments.length === 0">
                              <tr class="border-b">
                                <td class="p-3 text-sm">
                                  <p class="text-muted-foreground">
                                    {{ t('noAttachments') || 'No attachments added yet.' }}
                                  </p>
                                </td>
                              </tr>
                            </template>
                            <template v-else>
                              <tr
                                v-for="attachment in emailAttachments"
                                :key="getAttachmentKey(attachment)"
                                class="border-b"
                                :class="{
                                  'bg-amber-50':
                                    attachmentHasUnfilledRequiredFields(attachment) ||
                                    attachmentHasMissingSignatures(attachment)
                                }"
                              >
                                <td class="w-1/2 p-3">
                                  <div class="flex items-center py-1">
                                    <div class="flex-1">
                                      <div class="font-medium">
                                        {{ formatAttachmentName(attachment) }}
                                      </div>
                                      <div
                                        v-if="attachmentHasUnfilledRequiredFields(attachment)"
                                        class="mt-1 flex items-center gap-1 text-sm text-amber-700"
                                      >
                                        <InfoIcon class="h-4 w-4" />
                                        <span>{{ t('requiredFieldsNotFilled') }}</span>
                                      </div>
                                      <div
                                        v-if="attachmentHasMissingSignatures(attachment)"
                                        class="mt-1 flex items-center gap-1 text-sm text-amber-700"
                                      >
                                        <InfoIcon class="h-4 w-4" />
                                        <span>{{ t('signaturesNotFilled') }}</span>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td class="w-1/4 p-3">
                                  <div
                                    :class="
                                      'badge ' + getDocumentBadgeColor(attachment.documentType.id)
                                    "
                                    class="rounded px-2 py-1 text-[10px] font-semibold text-white"
                                  >
                                    {{ attachment.documentType.name }}
                                  </div>
                                </td>
                                <td class="w-1/4 p-3 text-right">
                                  <div class="flex items-center justify-end gap-3">
                                    <div
                                      v-if="
                                        attachment.documentType.id ===
                                        DocumentTypeIdEnum.PDF_TEMPLATE
                                      "
                                    >
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        class="h-10 w-[60px] px-4 py-2 text-gray-500 hover:bg-gray-500 hover:text-white"
                                        @click="openPdfEditor(attachment)"
                                      >
                                        <PencilIcon class="h-5 w-5" />
                                      </Button>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      class="h-10 w-[60px] px-4 py-2 text-red-500 hover:bg-red-500 hover:text-white"
                                      @click="removeAttachment(attachment)"
                                    >
                                      <TrashIcon class="h-5 w-5" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <!-- Client Email Warning -->
                    <div
                      v-if="
                        !caseRecord?.data?.client?.email ||
                        caseRecord.data.client.email.trim() === ''
                      "
                      class="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800"
                    >
                      <div class="flex items-center">
                        <InfoIcon class="mr-2 h-4 w-4" />
                        <span class="text-sm font-medium">
                          {{ t('noClientEmailWarning') }}
                        </span>
                      </div>
                    </div>

                    <!-- Footer with Send Button -->
                    <div class="flex justify-end space-x-2 pt-2">
                      <Button
                        variant="default"
                        class="h-9 px-4 text-white"
                        data-cy="emailCardSendButton"
                        :disabled="isSendingEmail || emailSubject === '' || emailContent === ''"
                        @click="handleSendEmail"
                      >
                        <template v-if="isSendingEmail">
                          <span
                            class="spinner-border spinner-border-sm me-3"
                            aria-hidden="true"
                            role="status"
                          ></span>
                        </template>
                        {{ t('send') || 'Send' }}
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            <div
              id="email-history"
              class="mt-4"
            >
              <Accordion
                v-model="emailHistoryAccordionValue"
                type="single"
                collapsible
                class="w-full"
              >
                <AccordionItem
                  value="email-history"
                  class="border-0 bg-transparent"
                >
                  <AccordionHeader class="flex items-center justify-between px-0 py-2">
                    <AccordionTrigger class="w-full py-0 hover:no-underline">
                      <div class="flex w-full items-center justify-between">
                        <h3 class="text-md font-medium">
                          {{ t('emailHistory') || 'Email History' }}
                        </h3>
                      </div>
                    </AccordionTrigger>
                  </AccordionHeader>
                  <AccordionContent class="px-0 pb-4 pt-2">
                    <!-- Loading state -->
                    <div
                      v-if="isLoadingEmails"
                      class="flex justify-center py-4"
                    >
                      <div
                        class="spinner-border text-primary"
                        role="status"
                      >
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </div>

                    <!-- Empty state -->
                    <div
                      v-else-if="emails.length === 0"
                      class="space-y-3"
                      data-cy="noEmailsHint"
                    >
                      <p class="text-sm text-muted-foreground">
                        {{ t('noEmailHistory') || 'No email history available.' }}
                      </p>
                    </div>

                    <!-- Email list -->
                    <div
                      v-else
                      class="space-y-4"
                    >
                      <div
                        v-for="email in emails"
                        :id="email.uuid"
                        :key="email.uuid"
                        class="card overflow-hidden rounded-md border shadow-sm"
                        data-cy="emailHistoryItem"
                      >
                        <div class="p-4">
                          <!-- Header with Avatar, Title, Date and Download -->
                          <div class="mb-1 flex items-start justify-between">
                            <div class="flex min-w-0 flex-1 items-start">
                              <div
                                class="avatar mr-3 flex h-10 w-10 shrink-0 items-center justify-center"
                              >
                                {{ getInitials(email.from_email.name) }}
                              </div>
                              <div class="min-w-0 flex-1">
                                <div
                                  class="truncate text-lg font-bold leading-tight"
                                  data-cy="emailSubject"
                                >
                                  {{ email.subject }}
                                </div>
                                <div class="text-sm leading-relaxed text-gray-700">
                                  <span class="font-medium">Von:</span>
                                  {{ email.from_email.name }} &lt;{{ email.from_email.email }}&gt;
                                </div>
                              </div>
                            </div>
                            <div class="ml-4 flex items-center gap-3">
                              <span class="whitespace-nowrap text-xs text-gray-600">
                                {{ formatDocumentDate(email.sent_at) }}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                class="h-8 w-8 shrink-0 p-0 text-gray-500 hover:text-gray-700"
                                :title="t('download') || 'Herunterladen'"
                                @click="openEmailInNewTab(email)"
                              >
                                <CloudDownloadIcon class="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          <!-- To and Status Row -->
                          <div class="flex items-center justify-between">
                            <div
                              v-if="email.to && email.to.length > 0"
                              class="min-w-0 flex-1 text-sm text-gray-700"
                            >
                              <span class="font-medium">An:</span>
                              <span class="ml-1 truncate">
                                <template
                                  v-for="(recipient, index) in email.to"
                                  :key="recipient.email"
                                >
                                  {{ recipient.name }} &lt;{{ recipient.email }}&gt;
                                  <span v-if="index < email.to.length - 1">,</span>
                                </template>
                              </span>
                            </div>

                            <!-- Tracking Status -->
                            <div class="ml-4 flex shrink-0 items-center">
                              <!-- Pending State for Recently Sent Emails -->
                              <div
                                v-if="
                                  pendingEmailUuids.has(email.uuid) &&
                                  caseRecord.org.track_email_delivery_events
                                "
                                class="flex cursor-pointer items-center gap-1 rounded-sm bg-blue-100 px-2 py-1 text-xs font-medium
                                  text-blue-800 transition-colors hover:bg-blue-200"
                                :title="t('deliveryEventPending')"
                                @click="openDeliveryStatusModal(email)"
                              >
                                <Loader2 class="h-3 w-3 animate-spin" />
                                {{ t('deliveryEventPending') }}
                              </div>
                              <!-- Actual Delivery Status -->
                              <div
                                v-else-if="
                                  email.relevant_delivery_event &&
                                  caseRecord.org.track_email_delivery_events
                                "
                                class="flex cursor-pointer items-center gap-1 rounded-sm px-2 py-1 text-xs font-medium transition-opacity
                                  hover:opacity-80"
                                :class="getDeliveryEventStyle(email.relevant_delivery_event.event)"
                                @click="openDeliveryStatusModal(email)"
                              >
                                <component
                                  :is="getDeliveryEventIcon(email.relevant_delivery_event.event)"
                                  class="h-3 w-3"
                                />
                                {{ getDeliveryEventLabel(email.relevant_delivery_event.event) }}
                              </div>
                            </div>
                          </div>

                          <!-- Email Content -->
                          <div class="mt-2 border-t pt-3">
                            <div
                              class="default-styled-container m-2"
                              data-cy="emailContent"
                              v-html="email.html"
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </div>
      <div class="fixed bottom-0 right-0 mb-4 mr-4">
        <AutoSavingIndicator />
      </div>
    </div>
    <div
      v-else
      class="container px-0"
    >
      <!-- Skeleton Loader -->
      <div>
        <!-- Header Skeleton -->
        <div class="flex flex-row flex-wrap items-start justify-between pt-4 md:flex-nowrap">
          <div class="ml-0 mt-2 h-auto grow">
            <div
              id="title"
              class="position-relative align-items-center justify-content-start flex gap-3"
            >
              <h1 class="text-h1 font-weight-bold flex items-center gap-3 text-nowrap">
                {{ t('requestFrom') }}
                <SkeletonLoader
                  height="32px"
                  width="150px"
                />
              </h1>
              <SkeletonLoader
                class="badge"
                height="24px"
                width="80px"
              />
            </div>
            <p class="flex items-center gap-2 text-sm text-stone-500">
              <SkeletonLoader
                height="16px"
                width="120px"
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                aria-hidden="true"
                data-slot="icon"
                class="inline size-4"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"
                ></path>
              </svg>
              Erstellt am
              <SkeletonLoader
                height="16px"
                width="180px"
              />
            </p>
          </div>
          <div
            id="options"
            class="my-3 flex h-full items-start justify-end gap-2"
          >
            <SkeletonLoader
              height="36px"
              width="180px"
            />
            <SkeletonLoader
              height="36px"
              width="40px"
            />
          </div>
        </div>

        <!-- Navigation Tabs Skeleton -->
        <div class="w-full border-b border-gray-200 pb-0">
          <nav class="flex w-full flex-wrap gap-x-1 overflow-hidden pt-1">
            <button
              class="flex-shrink-0 flex-grow-0 cursor-pointer items-center gap-x-1 border-b-2 border-purple-600 py-2 pr-1
                text-sm font-semibold text-purple-600"
            >
              {{ t('general') }}
            </button>
            <button
              v-if="user?.org?.enable_law_specific_data"
              class="flex-shrink-0 flex-grow-0 cursor-pointer items-center gap-x-1 border-b-2 border-transparent px-1
                py-2 text-sm"
            >
              <span class="flex items-center gap-2">
                {{ t('lawSpecificData') }}
                <SkeletonLoader
                  class="badge-square"
                  height="20px"
                  width="20px"
                />
              </span>
            </button>
            <button
              v-if="user?.org?.enable_law_specific_data"
              class="flex-shrink-0 flex-grow-0 cursor-pointer items-center gap-x-1 border-b-2 border-transparent px-1
                py-2 text-sm"
            >
              <span class="flex items-center gap-2">
                {{ t('entities') }}
                <SkeletonLoader
                  class="badge-square"
                  height="20px"
                  width="20px"
                />
              </span>
            </button>
            <button
              v-if="newPdfEditorEnabled.isEnabled.value"
              class="flex-shrink-0 flex-grow-0 cursor-pointer items-center gap-x-1 border-b-2 border-transparent px-1
                py-2 text-sm"
            >
              <span class="flex items-center gap-2">
                {{ t('documents') }}
                <BetaBadge />
              </span>
            </button>
            <button
              v-if="user?.org?.enable_law_specific_data"
              class="flex-shrink-0 flex-grow-0 cursor-pointer items-center gap-x-1 border-b-2 border-transparent px-1
                py-2 text-sm"
            >
              {{ t('aiChat') }}
            </button>
          </nav>
        </div>

        <!-- Top Form Row Skeleton -->
        <div class="row pb-3">
          <div class="flex flex-row flex-wrap gap-3">
            <div class="mb-3 flex-1">
              <label class="text-sm">{{ t('lawField') }}</label>
              <SkeletonLoader height="40px" />
            </div>
            <div class="mb-3 flex-1">
              <label class="text-sm">{{ t('lawTopic') }}</label>
              <SkeletonLoader height="40px" />
            </div>
            <div class="mb-3 flex-1">
              <label class="text-sm">{{ t('clerk') }}</label>
              <SkeletonLoader height="40px" />
            </div>
          </div>
        </div>

        <div class="row pb-3">
          <!-- Left Column Skeleton -->
          <div class="col-12 col-lg-4 mt-lg-0">
            <!-- Client Card Skeleton -->
            <div
              class="card mb-3"
              data-cy="clientCard"
            >
              <h3
                class="accordion-button mb-0 bg-white"
                aria-expanded="true"
                data-cy="clientCardHeader"
              >
                {{ t('client') }}
              </h3>
              <div class="card-body">
                <div class="space-y-3">
                  <div>
                    <label class="text-sm">{{ t('legalPerson') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div>
                    <label class="text-sm">{{ t('title') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div>
                    <label class="text-sm">{{ t('firstName') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div>
                    <label class="text-sm">{{ t('lastName') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div>
                    <label class="text-sm">{{ t('email') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div>
                    <label class="text-sm">{{ t('phone') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div
                    class="d-flex"
                    style="gap: 0.25rem"
                  >
                    <div style="flex: 3">
                      <label class="text-sm">{{ t('street') }}</label>
                      <SkeletonLoader height="40px" />
                    </div>
                    <div style="flex: 1">
                      <label class="text-sm">{{ t('houseNumber') }}</label>
                      <SkeletonLoader height="40px" />
                    </div>
                  </div>
                  <div
                    class="d-flex"
                    style="gap: 0.25rem"
                  >
                    <div style="flex: 1">
                      <label class="text-sm">{{ t('postalCode') }}</label>
                      <SkeletonLoader height="40px" />
                    </div>
                    <div style="flex: 3">
                      <label class="text-sm">{{ t('city') }}</label>
                      <SkeletonLoader height="40px" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Opponent Card Skeleton -->
            <div
              class="card mb-3"
              data-cy="opponentCard"
            >
              <h3
                class="accordion-button collapsed mb-0 bg-white"
                aria-expanded="false"
                data-cy="opponentCardHeader"
              >
                {{ t('opponent') }}
              </h3>
            </div>

            <!-- Legal Insurance Card Skeleton -->
            <div
              class="card mb-3"
              data-cy="legalInsuranceCard"
            >
              <div class="card-body">
                <div class="space-y-3">
                  <div class="flex items-center gap-2">
                    <SkeletonLoader
                      height="20px"
                      width="20px"
                    />
                    <label class="text-sm">{{ t('legalInsurance') }}</label>
                  </div>
                  <div>
                    <label class="text-sm">{{ t('legalInsuranceName') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div>
                    <label class="text-sm">{{ t('legalInsuranceNumber') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Internal Notes Card Skeleton -->
            <div
              class="card"
              data-cy="internalNotesCard"
            >
              <div class="card-body">
                <div>
                  <label class="text-sm">{{ t('internalNotes') }}</label>
                  <SkeletonLoader
                    class="mt-2"
                    height="160px"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column Skeleton -->
          <div class="col-12 col-lg-8">
            <div class="flex-column flex gap-3">
              <!-- Inquiry Card Skeleton -->
              <div
                class="card mt-3 lg:mt-0"
                data-cy="inquiryCard"
              >
                <div class="card-header block">
                  <div class="row">
                    <div class="col-6">
                      <h3 class="card-title pt-1">{{ t('inquiry') }}</h3>
                    </div>
                    <div class="col-6">
                      <SkeletonLoader
                        class="float-end"
                        height="40px"
                        width="192px"
                      />
                    </div>
                  </div>
                </div>
                <div class="card-body">
                  <div class="space-y-2">
                    <SkeletonLoader height="16px" />
                    <SkeletonLoader height="16px" />
                    <SkeletonLoader
                      height="16px"
                      width="75%"
                    />
                  </div>
                </div>
              </div>

              <!-- Appointments Card Skeleton -->
              <div
                class="card"
                data-cy="appointmentsCard"
              >
                <div class="card-header block">
                  <div class="row">
                    <div class="col-6">
                      <h3 class="card-title pt-1">{{ t('appointments') }}</h3>
                    </div>
                    <div class="col-6">
                      <span class="float-end">
                        <SkeletonLoader
                          height="40px"
                          width="192px"
                        />
                      </span>
                    </div>
                  </div>
                </div>
                <div class="card-body p-0">
                  <div class="table-responsive mb-0">
                    <table class="table-vcenter card-table table">
                      <tbody>
                        <tr class="hover:bg-gray-50">
                          <td class="w-1/3">
                            <div class="flex items-center py-1">
                              <div class="flex-1 space-y-2">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="20px"
                                  width="140px"
                                />
                              </div>
                            </div>
                          </td>
                          <td class="w-1/3">
                            <div class="flex items-center py-1">
                              <div class="flex-1 space-y-2">
                                <SkeletonLoader
                                  height="20px"
                                  width="100px"
                                />
                                <SkeletonLoader
                                  height="20px"
                                  width="130px"
                                />
                              </div>
                            </div>
                          </td>
                          <td class="w-1/3">
                            <div class="flex items-center py-1">
                              <div class="flex flex-1 items-center justify-between gap-3">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="32px"
                                  width="80px"
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                        <tr class="hover:bg-gray-50">
                          <td class="w-1/3">
                            <div class="flex items-center py-1">
                              <div class="flex-1 space-y-2">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="20px"
                                  width="140px"
                                />
                              </div>
                            </div>
                          </td>
                          <td class="w-1/3">
                            <div class="flex items-center py-1">
                              <div class="flex-1 space-y-2">
                                <SkeletonLoader
                                  height="20px"
                                  width="100px"
                                />
                                <SkeletonLoader
                                  height="20px"
                                  width="130px"
                                />
                              </div>
                            </div>
                          </td>
                          <td class="w-1/3">
                            <div class="flex items-center py-1">
                              <div class="flex flex-1 items-center justify-between gap-3">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="32px"
                                  width="80px"
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Received Documents Card Skeleton -->
              <div
                id="received-documents"
                class="card"
                style="z-index: 0"
              >
                <Accordion
                  type="single"
                  collapsible
                  class="w-full"
                  defaultValue="received-documents"
                >
                  <AccordionItem value="received-documents">
                    <AccordionHeader class="card-header flex grow">
                      <AccordionTrigger class="w-full py-0 hover:no-underline">
                        <div class="flex w-full items-center justify-between">
                          <h3 class="card-title flex items-center gap-2 pe-2">
                            {{ t('receivedDocuments') }}
                            <SkeletonLoader
                              class="badge bg-danger"
                              height="20px"
                              width="24px"
                            />
                          </h3>
                        </div>
                        <div class="flex items-center">
                          <SkeletonLoader
                            class="me-2"
                            height="32px"
                            width="48px"
                          />
                        </div>
                      </AccordionTrigger>
                    </AccordionHeader>
                    <AccordionContent class="px-4 pb-4">
                      <table class="table-vcenter card-table table table-fixed">
                        <tbody>
                          <tr class="hover:bg-gray-50">
                            <td class="w-1/2">
                              <div class="flex items-center gap-3 py-1">
                                <SkeletonLoader
                                  height="40px"
                                  width="40px"
                                />
                                <div class="min-w-0 flex-1 space-y-1">
                                  <SkeletonLoader
                                    height="16px"
                                    width="200px"
                                  />
                                  <SkeletonLoader
                                    height="14px"
                                    width="150px"
                                  />
                                </div>
                              </div>
                            </td>
                            <td class="w-1/4">
                              <div class="flex flex-col space-y-1">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="14px"
                                  width="140px"
                                />
                              </div>
                            </td>
                            <td class="w-1/4 text-right">
                              <div class="flex justify-end gap-2">
                                <SkeletonLoader
                                  height="36px"
                                  width="36px"
                                />
                                <SkeletonLoader
                                  height="36px"
                                  width="36px"
                                />
                              </div>
                            </td>
                          </tr>
                          <tr class="hover:bg-gray-50">
                            <td class="w-1/2">
                              <div class="flex items-center gap-3 py-1">
                                <SkeletonLoader
                                  height="40px"
                                  width="40px"
                                />
                                <div class="min-w-0 flex-1 space-y-1">
                                  <SkeletonLoader
                                    height="16px"
                                    width="180px"
                                  />
                                  <SkeletonLoader
                                    height="14px"
                                    width="130px"
                                  />
                                </div>
                              </div>
                            </td>
                            <td class="w-1/4">
                              <div class="flex flex-col space-y-1">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="14px"
                                  width="140px"
                                />
                              </div>
                            </td>
                            <td class="w-1/4 text-right">
                              <div class="flex justify-end gap-2">
                                <SkeletonLoader
                                  height="36px"
                                  width="36px"
                                />
                                <SkeletonLoader
                                  height="36px"
                                  width="36px"
                                />
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              <!-- Requested Documents Card Skeleton -->
              <div
                id="requested-documents"
                class="card"
                style="z-index: 0"
              >
                <Accordion
                  type="single"
                  collapsible
                  class="w-full"
                  defaultValue="requested-documents"
                >
                  <AccordionItem value="requested-documents">
                    <AccordionHeader class="card-header flex grow">
                      <AccordionTrigger class="w-full py-0 hover:no-underline">
                        <div class="flex w-full items-center justify-between">
                          <h3 class="card-title flex items-center gap-2 pe-2">
                            {{ t('requestedDocuments') }}
                            <SkeletonLoader
                              class="badge bg-danger"
                              height="20px"
                              width="24px"
                            />
                          </h3>
                        </div>
                        <div class="flex items-center">
                          <SkeletonLoader
                            class="me-2"
                            height="32px"
                            width="48px"
                          />
                        </div>
                      </AccordionTrigger>
                    </AccordionHeader>
                    <AccordionContent class="px-4 pb-4">
                      <table class="table-vcenter card-table table">
                        <tbody>
                          <tr class="hover:bg-gray-50">
                            <td class="w-1/2">
                              <div class="flex items-center gap-3 py-1">
                                <SkeletonLoader
                                  height="40px"
                                  width="40px"
                                />
                                <div class="min-w-0 flex-1 space-y-1">
                                  <SkeletonLoader
                                    height="16px"
                                    width="200px"
                                  />
                                  <SkeletonLoader
                                    height="14px"
                                    width="150px"
                                  />
                                </div>
                              </div>
                            </td>
                            <td class="w-1/4">
                              <div class="flex flex-col space-y-1">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="14px"
                                  width="140px"
                                />
                              </div>
                            </td>
                            <td class="w-1/4 text-right">
                              <div class="flex justify-end gap-2">
                                <SkeletonLoader
                                  height="36px"
                                  width="36px"
                                />
                              </div>
                            </td>
                          </tr>
                          <tr class="hover:bg-gray-50">
                            <td class="w-1/2">
                              <div class="flex items-center gap-3 py-1">
                                <SkeletonLoader
                                  height="40px"
                                  width="40px"
                                />
                                <div class="min-w-0 flex-1 space-y-1">
                                  <SkeletonLoader
                                    height="16px"
                                    width="180px"
                                  />
                                  <SkeletonLoader
                                    height="14px"
                                    width="130px"
                                  />
                                </div>
                              </div>
                            </td>
                            <td class="w-1/4">
                              <div class="flex flex-col space-y-1">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="14px"
                                  width="140px"
                                />
                              </div>
                            </td>
                            <td class="w-1/4 text-right">
                              <div class="flex justify-end gap-2">
                                <SkeletonLoader
                                  height="36px"
                                  width="36px"
                                />
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              <!-- Email Card Skeleton -->
              <div
                id="email"
                class="card"
                style="z-index: 0"
              >
                <Accordion
                  type="single"
                  collapsible
                  class="w-full"
                  defaultValue="email"
                >
                  <AccordionItem value="email">
                    <AccordionHeader class="card-header flex grow">
                      <AccordionTrigger class="w-full py-0 hover:no-underline">
                        <div class="flex w-full items-center justify-between">
                          <h3 class="card-title pe-2">
                            {{ t('email') }}
                          </h3>
                        </div>
                      </AccordionTrigger>
                    </AccordionHeader>
                    <AccordionContent
                      class="px-4 pb-4"
                      :overflow="'visible'"
                    >
                      <div class="space-y-4">
                        <div class="mb-4">
                          <div
                            class="mt-4 flex w-full items-center overflow-hidden rounded-[7px] border border-gray-200"
                          >
                            <label
                              for="email-template"
                              class="m-0 h-10 cursor-default bg-gray-50 px-3 text-sm"
                            >
                              {{ t('selectTemplate') }}
                            </label>
                            <div class="flex-1 px-2">
                              <SkeletonLoader height="32px" />
                            </div>
                          </div>
                        </div>
                        <div>
                          <label class="text-sm">Betreff</label>
                          <SkeletonLoader height="40px" />
                        </div>
                        <SkeletonLoader height="128px" />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              <!-- Email History Skeleton -->
              <div
                id="email-history"
                class="mt-4"
              >
                <Accordion
                  type="single"
                  collapsible
                  class="w-full"
                >
                  <AccordionItem
                    value="email-history"
                    class="border-0 bg-transparent"
                  >
                    <AccordionHeader class="flex items-center justify-between px-0 py-2">
                      <AccordionTrigger class="w-full py-0 hover:no-underline">
                        <div class="flex w-full items-center justify-between">
                          <h3 class="text-md font-medium">
                            {{ t('emailHistory') }}
                          </h3>
                        </div>
                      </AccordionTrigger>
                    </AccordionHeader>
                    <AccordionContent class="px-0 pb-4 pt-2">
                      <div class="space-y-4">
                        <div class="card overflow-hidden rounded-md border shadow-sm">
                          <div class="p-4">
                            <div class="mb-1 flex items-start justify-between">
                              <div class="flex min-w-0 flex-1 items-start">
                                <SkeletonLoader
                                  class="mr-3"
                                  height="40px"
                                  width="40px"
                                  type="circle"
                                />
                                <div class="min-w-0 flex-1 space-y-2">
                                  <SkeletonLoader
                                    height="18px"
                                    width="250px"
                                  />
                                  <SkeletonLoader
                                    height="14px"
                                    width="300px"
                                  />
                                </div>
                              </div>
                              <div class="ml-4 flex items-center gap-3">
                                <SkeletonLoader
                                  height="12px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="32px"
                                  width="32px"
                                />
                              </div>
                            </div>
                            <div class="mt-2 space-y-2">
                              <SkeletonLoader
                                height="14px"
                                width="100%"
                              />
                              <SkeletonLoader
                                height="14px"
                                width="100%"
                              />
                              <SkeletonLoader
                                height="14px"
                                width="80%"
                              />
                            </div>
                          </div>
                        </div>
                        <div class="card overflow-hidden rounded-md border shadow-sm">
                          <div class="p-4">
                            <div class="mb-1 flex items-start justify-between">
                              <div class="flex min-w-0 flex-1 items-start">
                                <SkeletonLoader
                                  class="mr-3"
                                  height="40px"
                                  width="40px"
                                  type="circle"
                                />
                                <div class="min-w-0 flex-1 space-y-2">
                                  <SkeletonLoader
                                    height="18px"
                                    width="220px"
                                  />
                                  <SkeletonLoader
                                    height="14px"
                                    width="280px"
                                  />
                                </div>
                              </div>
                              <div class="ml-4 flex items-center gap-3">
                                <SkeletonLoader
                                  height="12px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="32px"
                                  width="32px"
                                />
                              </div>
                            </div>
                            <div class="mt-2 space-y-2">
                              <SkeletonLoader
                                height="14px"
                                width="100%"
                              />
                              <SkeletonLoader
                                height="14px"
                                width="95%"
                              />
                              <SkeletonLoader
                                height="14px"
                                width="70%"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </SpaCompatComponent>

  <CreateAppointmentModal
    v-model:open="isAppointmentCreateModalOpen"
    :case="
      caseRecord?.uuid
        ? {
            uuid: caseRecord.uuid,
            services: Array.isArray(caseRecord.services) ? caseRecord.services : []
          }
        : { uuid: '', services: [] }
    "
    @appointment-created="
      async () => {
        refreshCaseRecord()
        await fetchEmails()
      }
    "
  />

  <!-- Upload Document Model -->
  <Dialog
    :open="isDocumentUploadModalOpen"
    @update:open="isDocumentUploadModalOpen = $event"
  >
    <DialogContent class="sm:max-w-[600px]">
      <DialogHeader>
        <DialogTitle>{{ t('uploadDocuments') }}</DialogTitle>
      </DialogHeader>
      <DialogDescription>
        {{ t('uploadDocumentsDescription') }}
      </DialogDescription>

      <div class="py-4">
        <DocumentUpload
          v-if="caseRecord"
          :case-data="caseRecord"
          :auto-remove-after-upload="false"
          @file-uploaded="handleFileUploaded"
        />
      </div>

      <DialogFooter>
        <Button
          variant="secondary"
          @click="isDocumentUploadModalOpen = false"
        >
          {{ t('cancel') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Unified Document Template Selection Modal for Link generation -->
  <TemplateDialog
    v-model="showUnifiedTemplateModal"
    :title="t('documentTemplate')"
    :description="t('createLinkDocumentTemplateDescription')"
    :items="unifiedTemplates"
    :is-loading="loadingUnifiedTemplates"
    :search-placeholder="t('searchTemplate')"
    @select-item="createLinkUnifiedTemplate"
    @preview-item="handleUnifiedTemplatePreview"
  />

  <!-- Questionnaire Selection Modal for link generation -->
  <TemplateDialog
    v-model="showQuestionnaireModal"
    :title="t('questionnaire')"
    :description="t('createLinkQuestionnaireDescription')"
    :items="questionnaires"
    :is-loading="loadingQuestionnaires"
    :search-placeholder="t('searchQuestionnaire')"
    @select-item="createLinkQuestionnaire"
    @preview-item="handleQuestionnairePreview"
  />

  <!-- Document Template Selection Dialogs for Email attachments -->
  <SelectPdfFormsDialog
    v-model:show="showDocumentAttachDialog"
    :case-uuid="caseRecord?.uuid"
    :initial-selected-documents="
      emailAttachments
        .filter(
          (attachment) =>
            attachment.documentType?.id === DocumentTypeIdEnum.PDF_TEMPLATE &&
            attachment.documentProperties
        )
        .map((pdfAttachment) => {
          const pdfAttachmentProps = pdfAttachment.documentProperties as FormDocumentProperties
          // Create a TForm object from the document properties
          return {
            id: pdfAttachmentProps.id,
            organisation: pdfAttachmentProps.organisation,
            created_at: pdfAttachmentProps.created_at,
            modified_at: pdfAttachmentProps.modified_at,
            tag: pdfAttachmentProps.tag,
            title: pdfAttachmentProps.title,
            description: pdfAttachmentProps.description,
            file: pdfAttachmentProps.file,
            due_at: pdfAttachmentProps.due_at,
            document_name: pdfAttachmentProps.document_name
          } as TForm
        })
    "
    @select="handleDocumentsSelect"
  />

  <!-- Questionnaire Selection Dialogs for Email attachments -->
  <SelectDocumentsDialog
    v-model="showQuestionnaireAttachDialog"
    :title="t('questionnaireTitle')"
    :description="t('questionnaireDescription')"
    :items="questionnaires"
    :is-loading="loadingQuestionnaires"
    :search-placeholder="t('searchQuestionnaire')"
    :initial-selected-items="
      emailAttachments
        .filter((attachment) => attachment.documentType?.id === DocumentTypeIdEnum.QUESTIONNAIRE)
        .map((attachment) => {
          const props = attachment.documentProperties as QuestionnaireDocumentProperties
          return props?.id ? questionnaires.find((q) => q.uuid === props.id) || null : null
        })
        .filter(Boolean)
    "
    @select-items="handleQuestionnairesSelect"
    @preview-item="handleQuestionnairePreview"
  />

  <PDFViewer
    v-model:isOpen="showPdfViewer"
    v-model:url="currentPdfUrl"
    :filename="currentPdfFilename"
  />

  <!-- PDFViewer as Editor Modal -->
  <div class="flex h-full w-full">
    <NewPdfViewer
      v-if="newPdfEditorEnabled.isEnabled.value"
      v-model:isOpen="showPdfEditor"
      :filename="(selectedDocument as any)?.filename || (selectedDocument as any)?.title || ''"
      :url="
        (selectedDocument as any)?.current_pdf_url ||
        (selectedDocument as any)?.file ||
        (selectedDocument as any)?.url ||
        ''
      "
      :hide-navigation="false"
      :annotation="true"
      :form-id="(selectedDocument as any)?.id || (selectedDocument as any)?.uuid || ''"
      :document="selectedDocument as any"
      @document-updated="handleDocumentUpdated"
    />
    <PDFViewer
      v-else
      v-model:isOpen="showPdfEditor"
      :filename="selectedDocument?.title || ''"
      :url="(selectedDocument as CustomerForm)?.file || ''"
      :loading="newPdfStore.isLoading"
      :hide-navigation="false"
      :annotation="true"
      :form-id="(selectedDocument as CustomerForm)?.id || ''"
      :document="{ filesize: 0, page_count: 0 }"
    />

    <SignModal v-if="showPdfEditor && signatureStore.showSignModal" />
  </div>

  <!-- Office Document Preview Modal -->
  <Dialog
    v-if="officeEditingFlag.isEnabled"
    v-model:open="showOfficePreview"
  >
    <DialogContent class="h-[85vh] w-full max-w-5xl overflow-hidden p-0">
      <div class="flex h-full flex-col">
        <DialogHeader class="border-b px-6 py-4">
          <DialogTitle>
            {{
              currentOfficeDocument?.title || currentOfficeDocument?.filename || 'Document Preview'
            }}
          </DialogTitle>
        </DialogHeader>
        <div class="w-full flex-1 overflow-hidden">
          <EmbeddableOfficePreview
            v-if="currentOfficeDocument"
            :document="currentOfficeDocument"
            height="100%"
            :allow-download="false"
            :allow-print="false"
            :show-border="false"
          />
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Questionnaire Submission Modal -->
  <Dialog
    :open="showQuestionnaireSubmissionModal"
    @update:open="showQuestionnaireSubmissionModal = $event"
  >
    <DialogContent class="gap-0 sm:max-w-[800px]">
      <DialogHeader class="border-b pb-3">
        <DialogTitle>{{ selectedQuestionnaireSubmission?.title || '' }}</DialogTitle>
      </DialogHeader>
      <div class="max-h-[60vh] overflow-y-auto px-2">
        <table class="w-full">
          <tbody>
            <template
              v-if="
                selectedQuestionnaireSubmission?.schema &&
                Array.isArray(selectedQuestionnaireSubmission.schema)
              "
            >
              <template
                v-for="(section, sectionIdx) in selectedQuestionnaireSubmission.schema"
                :key="sectionIdx"
              >
                <h3 class="mt-3 text-lg font-semibold">{{ section?.title || '' }}</h3>
                <tr
                  v-for="(item, itemIdx) in section?.items || []"
                  :key="`${sectionIdx}-${itemIdx}`"
                  class="border-b"
                >
                  <td class="w-1/2 px-4 py-3 text-left align-top">
                    <div class="max-w-[230px] break-words font-medium">
                      {{ item?.label || '' }}
                      <span
                        v-if="item?.optional || true"
                        class="ml-1 rounded bg-green-100 px-1 py-0.5 text-xs text-green-800"
                      >
                        optional
                      </span>
                    </div>
                  </td>
                  <td class="w-1/2 p-3 font-normal">
                    <div>{{ item?.value || '' }}</div>
                  </td>
                </tr>
              </template>
            </template>
            <tr v-else>
              <td
                colspan="2"
                class="p-4 text-center text-gray-500"
              >
                {{ t('noDataAvailable') || 'No data available' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Delivery Status Modal -->
  <EmailDeliveryStatusModal
    :open="showDeliveryStatusModal"
    :email="selectedEmailForDeliveryStatus"
    @update:open="showDeliveryStatusModal = $event"
  />
</template>

<style>
  .badge-square {
    @apply inline-flex h-5 min-w-5 items-center justify-center overflow-hidden rounded-sm bg-[#ae3ec9] p-1 text-[0.625rem] font-semibold text-white;
  }

  /* Card styles from dashboard.css */
  .card {
    position: relative;
    display: flex;
    flex-direction: column;
    min-width: 0;
    word-wrap: break-word;
    background-color: #ffffff;
    background-clip: border-box;
    border: 1px solid rgba(98, 105, 118, 0.16);
    border-radius: 4px;
  }

  .card > hr,
  .card > .hr {
    margin-right: 0;
    margin-left: 0;
  }

  .card-body {
    flex: 1 1 auto;
    padding: 1rem 1rem;
  }

  .card-title {
    margin-bottom: 1.25rem;
  }

  .card-header {
    padding: 0.75rem 1rem;
    margin-bottom: 0;
    color: #051b35;
    background-color: #ffffff;
    border-bottom: 1px solid rgba(98, 105, 118, 0.16);
  }

  .card-header:first-child {
    border-radius: 3px 3px 0 0;
  }

  .card-footer {
    padding: 0.75rem 1rem;
    color: #051b35;
    background-color: #ffffff;
    border-top: 1px solid rgba(98, 105, 118, 0.16);
  }

  .card-footer:last-child {
    border-radius: 0 0 3px 3px;
  }

  label {
    padding: 5px 5px 5px 0;
  }

  .documents-edit-link {
    color: rgb(147 51 234);
    text-decoration: none;
    font-size: 0.875rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
  }

  .documents-edit-link:hover {
    color: rgb(147 51 234);
    text-decoration: none;
  }

  .link-text {
    border-bottom: 1px solid rgb(147 51 234);
    padding-bottom: 1px;
    line-height: 1;
    white-space: nowrap;
  }

  .neu-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: rgb(147 51 234);
    color: white;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.25rem 0.5rem;
    border-radius: 9999px;
    text-transform: uppercase;
    line-height: 1;
  }
</style>
